import {Injectable} from '@angular/core';
import {PermissionSet} from "./Models/PermissionSet";
import {AuthenticationService} from "./authentication.service";
import {Roles} from "./x-enums/roles";

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  /*
   * REMOVED: `GetDeactivatedPermission()` / `static deactivated = new PermissionSet("PlatformOwner", [])`.
   *
   * It did the opposite of what its name said. `CheckPermission` ends with `default.every(...)`, and `[].every()`
   * is `true` by definition, so a set with an empty `default` passes for EVERY signed-in session — the set that
   * read as "platform owners only" was in fact "everyone". Nothing ever called it (no component, template, route,
   * story or spec, in the three years since it arrived in a hotfix), so it was a trap rather than an open door:
   * whoever reached for it first, on the strength of its name, would have shipped a screen with no gate at all.
   *
   * Deleted rather than repaired, deliberately. Repairing it — e.g. `new PermissionSet(Roles.PlatformOwner,
   * [Roles.PlatformOwner])`, the shape `PermissionService.admin` already uses — would have kept a set whose
   * meaning nobody can state: there is no "deactivated" surface anywhere in the app, and the name says nothing
   * about who is supposed to pass. A gate with no stated subject is the same trap one refactor later. If a
   * deactivated-account screen ever needs one, write it then, against the vocabulary that exists then.
   *
   * The invariant behind it is pinned in `permission.service.spec.ts`: no PermissionSet on this class may carry
   * an empty `default` unless it also carries `anyRoles`.
   */

  /* copy start */


  Admin(): PermissionSet {
    return PermissionService.admin;
  }

  static admin = new PermissionSet("ShardAdmin", ["ShardAdmin"]);

  Administration_Contract_GetCurretCompany(): PermissionSet {
    return PermissionService.administration_contract_getcurretcompany;
  }

  static administration_contract_getcurretcompany = new PermissionSet("ShardAdmin", ["Administration.Contract.GetCurretCompany"]);

  Administration_MigrationAssistant_Event_Register_Patch_20231003(): PermissionSet {
    return PermissionService.administration_migrationassistant_event_register_patch_20231003;
  }

  static administration_migrationassistant_event_register_patch_20231003 = new PermissionSet("ShardAdmin", ["Administration.MigrationAssistant.Event_Register_Patch_20231003"]);

  Administration_MigrationAssistant_GetPatches(): PermissionSet {
    return PermissionService.administration_migrationassistant_getpatches;
  }

  static administration_migrationassistant_getpatches = new PermissionSet("ShardAdmin", ["Administration.MigrationAssistant.GetPatches"]);

  Administration_MigrationAssistant_InventoryNumber_Patch_20230909(): PermissionSet {
    return PermissionService.administration_migrationassistant_inventorynumber_patch_20230909;
  }

  static administration_migrationassistant_inventorynumber_patch_20230909 = new PermissionSet("ShardAdmin", ["Administration.MigrationAssistant.InventoryNumber_Patch_20230909"]);

  Administration_MigrationAssistant_Legal_Germany_Submission_PushDevices_Patch_20250729(): PermissionSet {
    return PermissionService.administration_migrationassistant_legal_germany_submission_pushdevices_patch_20250729;
  }

  static administration_migrationassistant_legal_germany_submission_pushdevices_patch_20250729 = new PermissionSet("ShardAdmin", ["Administration.MigrationAssistant.Legal_Germany_Submission_PushDevices_Patch_20250729"]);

  Administration_MigrationAssistant_Vat_Patch_20230912(): PermissionSet {
    return PermissionService.administration_migrationassistant_vat_patch_20230912;
  }

  static administration_migrationassistant_vat_patch_20230912 = new PermissionSet("ShardAdmin", ["Administration.MigrationAssistant.Vat_Patch_20230912"]);

  Administration_Permission_List(): PermissionSet {
    return PermissionService.administration_permission_list;
  }

  static administration_permission_list = new PermissionSet("ShardAdmin", ["Administration.Permission.List"]);

  Administration_Role_Add(): PermissionSet {
    return PermissionService.administration_role_add;
  }

  static administration_role_add = new PermissionSet("ShardAdmin", ["Administration.Role.Add"]);

  Administration_Role_AddUser(): PermissionSet {
    return PermissionService.administration_role_adduser;
  }

  static administration_role_adduser = new PermissionSet("ShardAdmin", ["Administration.Role.AddUser"]);

  Administration_Role_Delete(): PermissionSet {
    return PermissionService.administration_role_delete;
  }

  static administration_role_delete = new PermissionSet("ShardAdmin", ["Administration.Role.Delete"]);

  Administration_Role_Get(): PermissionSet {
    return PermissionService.administration_role_get;
  }

  static administration_role_get = new PermissionSet("ShardAdmin", ["Administration.Role.Get"]);

  Administration_Role_List(): PermissionSet {
    return PermissionService.administration_role_list;
  }

  static administration_role_list = new PermissionSet("ShardAdmin", ["Administration.Role.List"]);

  Administration_Role_RemoveUser(): PermissionSet {
    return PermissionService.administration_role_removeuser;
  }

  static administration_role_removeuser = new PermissionSet("ShardAdmin", ["Administration.Role.RemoveUser"]);

  Administration_Role_TogglePermission(): PermissionSet {
    return PermissionService.administration_role_togglepermission;
  }

  static administration_role_togglepermission = new PermissionSet("ShardAdmin", ["Administration.Role.TogglePermission"]);

  Administration_Settings_GetLogo(): PermissionSet {
    return PermissionService.administration_settings_getlogo;
  }

  static administration_settings_getlogo = new PermissionSet("ShardAdmin", ["Administration.Settings.GetLogo"]);

  Administration_Settings_GetShiftMakerSettings(): PermissionSet {
    return PermissionService.administration_settings_getshiftmakersettings;
  }

  static administration_settings_getshiftmakersettings = new PermissionSet("ShardAdmin", ["Administration.Settings.GetShiftMakerSettings"]);

  Administration_Settings_GetTimezone(): PermissionSet {
    return PermissionService.administration_settings_gettimezone;
  }

  static administration_settings_gettimezone = new PermissionSet("ShardAdmin", ["Administration.Settings.GetTimezone"]);

  Administration_Settings_SetLogo(): PermissionSet {
    return PermissionService.administration_settings_setlogo;
  }

  static administration_settings_setlogo = new PermissionSet("ShardAdmin", ["Administration.Settings.SetLogo"]);

  Administration_Settings_SetShiftMakerSettings(): PermissionSet {
    return PermissionService.administration_settings_setshiftmakersettings;
  }

  static administration_settings_setshiftmakersettings = new PermissionSet("ShardAdmin", ["Administration.Settings.SetShiftMakerSettings"]);

  Administration_Settings_SetTimezone(): PermissionSet {
    return PermissionService.administration_settings_settimezone;
  }

  static administration_settings_settimezone = new PermissionSet("ShardAdmin", ["Administration.Settings.SetTimezone"]);

  Administration_User_Add(): PermissionSet {
    return PermissionService.administration_user_add;
  }

  static administration_user_add = new PermissionSet("ShardAdmin", ["Administration.User.Add"]);

  Administration_User_Delete(): PermissionSet {
    return PermissionService.administration_user_delete;
  }

  static administration_user_delete = new PermissionSet("ShardAdmin", ["Administration.User.Delete"]);

  Administration_User_Get(): PermissionSet {
    return PermissionService.administration_user_get;
  }

  static administration_user_get = new PermissionSet("ShardAdmin", ["Administration.User.Get"]);

  Administration_User_List(): PermissionSet {
    return PermissionService.administration_user_list;
  }

  static administration_user_list = new PermissionSet("ShardAdmin", ["Administration.User.List"]);

  Administration_User_ListCompanyUser(): PermissionSet {
    return PermissionService.administration_user_listcompanyuser;
  }

  static administration_user_listcompanyuser = new PermissionSet("ShardAdmin", ["Administration.User.ListCompanyUser"]);

  Administration_User_ListOwners(): PermissionSet {
    return PermissionService.administration_user_listowners;
  }

  static administration_user_listowners = new PermissionSet("ShardAdmin", ["Administration.User.ListOwners"]);

  Analysis_EventAnalysis_FetchEventData(): PermissionSet {
    return PermissionService.analysis_eventanalysis_fetcheventdata;
  }

  static analysis_eventanalysis_fetcheventdata = new PermissionSet("ShardAdmin", ["Analysis.EventAnalysis.FetchEventData"]);

  Analysis_EventAnalysis_GetEventsLike(): PermissionSet {
    return PermissionService.analysis_eventanalysis_geteventslike;
  }

  static analysis_eventanalysis_geteventslike = new PermissionSet("ShardAdmin", ["Analysis.EventAnalysis.GetEventsLike"]);

  Automation_Scheduler_DeleteGroup(): PermissionSet {
    return PermissionService.automation_scheduler_deletegroup;
  }

  static automation_scheduler_deletegroup = new PermissionSet("ShardAdmin", ["Automation.Scheduler.DeleteGroup"]);

  Automation_Scheduler_GetSchedulerGroup(): PermissionSet {
    return PermissionService.automation_scheduler_getschedulergroup;
  }

  static automation_scheduler_getschedulergroup = new PermissionSet("ShardAdmin", ["Automation.Scheduler.GetSchedulerGroup"]);

  Automation_Scheduler_GetSchedulerObjects(): PermissionSet {
    return PermissionService.automation_scheduler_getschedulerobjects;
  }

  static automation_scheduler_getschedulerobjects = new PermissionSet("ShardAdmin", ["Automation.Scheduler.GetSchedulerObjects"]);

  Automation_Scheduler_ScheduleGroup(): PermissionSet {
    return PermissionService.automation_scheduler_schedulegroup;
  }

  static automation_scheduler_schedulegroup = new PermissionSet("ShardAdmin", ["Automation.Scheduler.ScheduleGroup"]);

  Automation_Workflow_Add(): PermissionSet {
    return PermissionService.automation_workflow_add;
  }

  static automation_workflow_add = new PermissionSet("ShardAdmin", ["Automation.Workflow.Add"]);

  Automation_Workflow_Delete(): PermissionSet {
    return PermissionService.automation_workflow_delete;
  }

  static automation_workflow_delete = new PermissionSet("ShardAdmin", ["Automation.Workflow.Delete"]);

  Automation_Workflow_Get(): PermissionSet {
    return PermissionService.automation_workflow_get;
  }

  static automation_workflow_get = new PermissionSet("ShardAdmin", ["Automation.Workflow.Get"]);

  Automation_Workflow_List(): PermissionSet {
    return PermissionService.automation_workflow_list;
  }

  static automation_workflow_list = new PermissionSet("ShardAdmin", ["Automation.Workflow.List"]);

  Automation_Workflow_SetValue(): PermissionSet {
    return PermissionService.automation_workflow_setvalue;
  }

  static automation_workflow_setvalue = new PermissionSet("ShardAdmin", ["Automation.Workflow.SetValue"]);

  Booking_Order_GetOrderCountFromShard(): PermissionSet {
    return PermissionService.booking_order_getordercountfromshard;
  }

  static booking_order_getordercountfromshard = new PermissionSet("ShardAdmin", ["Booking.Order.GetOrderCountFromShard"]);

  Booking_Order_List(): PermissionSet {
    return PermissionService.booking_order_list;
  }

  static booking_order_list = new PermissionSet("ShardAdmin", ["Booking.Order.List"]);

  Booking_Order_SetActiveState(): PermissionSet {
    return PermissionService.booking_order_setactivestate;
  }

  static booking_order_setactivestate = new PermissionSet("ShardAdmin", ["Booking.Order.SetActiveState"]);

  Booking_Reference_List(): PermissionSet {
    return PermissionService.booking_reference_list;
  }

  static booking_reference_list = new PermissionSet("ShardAdmin", ["Booking.Reference.List"]);

  Booking_Reference_Load(): PermissionSet {
    return PermissionService.booking_reference_load;
  }

  static booking_reference_load = new PermissionSet("ShardAdmin", ["Booking.Reference.Load"]);

  Booking_Reference_LoadActiveIdentifiers(): PermissionSet {
    return PermissionService.booking_reference_loadactiveidentifiers;
  }

  static booking_reference_loadactiveidentifiers = new PermissionSet("ShardAdmin", ["Booking.Reference.LoadActiveIdentifiers"]);

  Booking_Reference_Prepare(): PermissionSet {
    return PermissionService.booking_reference_prepare;
  }

  static booking_reference_prepare = new PermissionSet("ShardAdmin", ["Booking.Reference.Prepare"]);

  Booking_Reference_RegisterExpected(): PermissionSet {
    return PermissionService.booking_reference_registerexpected;
  }

  static booking_reference_registerexpected = new PermissionSet("ShardAdmin", ["Booking.Reference.RegisterExpected"]);

  Booking_Table_Add(): PermissionSet {
    return PermissionService.booking_table_add;
  }

  static booking_table_add = new PermissionSet("ShardAdmin", ["Booking.Table.Add"]);

  Booking_Table_Delete(): PermissionSet {
    return PermissionService.booking_table_delete;
  }

  static booking_table_delete = new PermissionSet("ShardAdmin", ["Booking.Table.Delete"]);

  Booking_Table_Get(): PermissionSet {
    return PermissionService.booking_table_get;
  }

  static booking_table_get = new PermissionSet("ShardAdmin", ["Booking.Table.Get"]);

  Booking_Table_List(): PermissionSet {
    return PermissionService.booking_table_list;
  }

  static booking_table_list = new PermissionSet("ShardAdmin", ["Booking.Table.List"]);

  Booking_Table_Upsert(): PermissionSet {
    return PermissionService.booking_table_upsert;
  }

  static booking_table_upsert = new PermissionSet("ShardAdmin", ["Booking.Table.Upsert"]);

  Booking_Table_UpsertList(): PermissionSet {
    return PermissionService.booking_table_upsertlist;
  }

  static booking_table_upsertlist = new PermissionSet("ShardAdmin", ["Booking.Table.UpsertList"]);

  Communication_Scheduler_AddTelegramMessage(): PermissionSet {
    return PermissionService.communication_scheduler_addtelegrammessage;
  }

  static communication_scheduler_addtelegrammessage = new PermissionSet("ShardAdmin", ["Communication.Scheduler.AddTelegramMessage"]);

  Communication_Scheduler_List(): PermissionSet {
    return PermissionService.communication_scheduler_list;
  }

  static communication_scheduler_list = new PermissionSet("ShardAdmin", ["Communication.Scheduler.List"]);

  Communication_Scheduler_ListTelegramMessages(): PermissionSet {
    return PermissionService.communication_scheduler_listtelegrammessages;
  }

  static communication_scheduler_listtelegrammessages = new PermissionSet("ShardAdmin", ["Communication.Scheduler.ListTelegramMessages"]);

  Communication_Scheduler_RemoveTelegramMessage(): PermissionSet {
    return PermissionService.communication_scheduler_removetelegrammessage;
  }

  static communication_scheduler_removetelegrammessage = new PermissionSet("ShardAdmin", ["Communication.Scheduler.RemoveTelegramMessage"]);

  Company_Company_GetCompanyShards(): PermissionSet {
    return PermissionService.company_company_getcompanyshards;
  }

  static company_company_getcompanyshards = new PermissionSet("ShardAdmin", ["Company.Company.GetCompanyShards"]);

  Company_Company_List(): PermissionSet {
    return PermissionService.company_company_list;
  }

  static company_company_list = new PermissionSet("ShardAdmin", ["Company.Company.List"]);

  Company_Employee_Add(): PermissionSet {
    return PermissionService.company_employee_add;
  }

  static company_employee_add = new PermissionSet("ShardAdmin", ["Company.Employee.Add"]);

  Company_Employee_AllowEmployeeLogin(): PermissionSet {
    return PermissionService.company_employee_allowemployeelogin;
  }

  static company_employee_allowemployeelogin = new PermissionSet("ShardAdmin", ["Company.Employee.AllowEmployeeLogin"]);

  Company_EmployeeAttribute_Add(): PermissionSet {
    return PermissionService.company_employeeattribute_add;
  }

  static company_employeeattribute_add = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.Add"]);

  Company_EmployeeAttribute_ChangeName(): PermissionSet {
    return PermissionService.company_employeeattribute_changename;
  }

  static company_employeeattribute_changename = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.ChangeName"]);

  Company_EmployeeAttribute_ChangeShortName(): PermissionSet {
    return PermissionService.company_employeeattribute_changeshortname;
  }

  static company_employeeattribute_changeshortname = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.ChangeShortName"]);

  Company_EmployeeAttribute_Delete(): PermissionSet {
    return PermissionService.company_employeeattribute_delete;
  }

  static company_employeeattribute_delete = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.Delete"]);

  Company_EmployeeAttribute_Get(): PermissionSet {
    return PermissionService.company_employeeattribute_get;
  }

  static company_employeeattribute_get = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.Get"]);

  Company_EmployeeAttribute_List(): PermissionSet {
    return PermissionService.company_employeeattribute_list;
  }

  static company_employeeattribute_list = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.List"]);

  Company_EmployeeAttribute_SetEmployeeAttributes(): PermissionSet {
    return PermissionService.company_employeeattribute_setemployeeattributes;
  }

  static company_employeeattribute_setemployeeattributes = new PermissionSet("ShardAdmin", ["Company.EmployeeAttribute.SetEmployeeAttributes"]);

  Company_Employee_ChangeName(): PermissionSet {
    return PermissionService.company_employee_changename;
  }

  static company_employee_changename = new PermissionSet("ShardAdmin", ["Company.Employee.ChangeName"]);

  Company_Employee_Delete(): PermissionSet {
    return PermissionService.company_employee_delete;
  }

  static company_employee_delete = new PermissionSet("ShardAdmin", ["Company.Employee.Delete"]);

  Company_Employee_EmployeeSalesList(): PermissionSet {
    return PermissionService.company_employee_employeesaleslist;
  }

  static company_employee_employeesaleslist = new PermissionSet("ShardAdmin", ["Company.Employee.EmployeeSalesList"]);

  Company_Employee_EmployeeSalesOrders(): PermissionSet {
    return PermissionService.company_employee_employeesalesorders;
  }

  static company_employee_employeesalesorders = new PermissionSet("ShardAdmin", ["Company.Employee.EmployeeSalesOrders"]);

  Company_Employee_Get(): PermissionSet {
    return PermissionService.company_employee_get;
  }

  static company_employee_get = new PermissionSet("ShardAdmin", ["Company.Employee.Get"]);

  Company_Employee_GetEmployeeShiftList(): PermissionSet {
    return PermissionService.company_employee_getemployeeshiftlist;
  }

  static company_employee_getemployeeshiftlist = new PermissionSet("ShardAdmin", ["Company.Employee.GetEmployeeShiftList"]);

  Company_Employee_GetEmploymentRelationships(): PermissionSet {
    return PermissionService.company_employee_getemploymentrelationships;
  }

  static company_employee_getemploymentrelationships = new PermissionSet("ShardAdmin", ["Company.Employee.GetEmploymentRelationships"]);

  Company_Employee_GetRestricted(): PermissionSet {
    return PermissionService.company_employee_getrestricted;
  }

  static company_employee_getrestricted = new PermissionSet("ShardAdmin", ["Company.Employee.GetRestricted"]);

  Company_Employee_GetStats(): PermissionSet {
    return PermissionService.company_employee_getstats;
  }

  static company_employee_getstats = new PermissionSet("ShardAdmin", ["Company.Employee.GetStats"]);

  Company_Employee_List(): PermissionSet {
    return PermissionService.company_employee_list;
  }

  static company_employee_list = new PermissionSet("ShardAdmin", ["Company.Employee.List"]);

  Company_Employee_ListMinimal(): PermissionSet {
    return PermissionService.company_employee_listminimal;
  }

  static company_employee_listminimal = new PermissionSet("ShardAdmin", ["Company.Employee.ListMinimal"]);

  Company_Employee_ListNl(): PermissionSet {
    return PermissionService.company_employee_listnl;
  }

  static company_employee_listnl = new PermissionSet("ShardAdmin", ["Company.Employee.ListNl"]);

  Company_Employee_RequestVerification(): PermissionSet {
    return PermissionService.company_employee_requestverification;
  }

  static company_employee_requestverification = new PermissionSet("ShardAdmin", ["Company.Employee.RequestVerification"]);

  Company_Employee_ResetSocial(): PermissionSet {
    return PermissionService.company_employee_resetsocial;
  }

  static company_employee_resetsocial = new PermissionSet("ShardAdmin", ["Company.Employee.ResetSocial"]);

  // Company_Employee_SendPasswordReset is gone (#279): the endpoint it gated no longer exists. A permission set
  // for an endpoint the server does not serve is a permission a role screen would keep offering to grant.

  Company_Employee_SetNewPassword(): PermissionSet {
    return PermissionService.company_employee_setnewpassword;
  }

  static company_employee_setnewpassword = new PermissionSet("ShardAdmin", ["Company.Employee.SetNewPassword"]);

  Company_Employee_SetPublicProperty(): PermissionSet {
    return PermissionService.company_employee_setpublicproperty;
  }

  static company_employee_setpublicproperty = new PermissionSet("ShardAdmin", ["Company.Employee.SetPublicProperty"]);

  Company_Employee_SetSocial(): PermissionSet {
    return PermissionService.company_employee_setsocial;
  }

  static company_employee_setsocial = new PermissionSet("ShardAdmin", ["Company.Employee.SetSocial"]);

  Company_Employee_SetValue(): PermissionSet {
    return PermissionService.company_employee_setvalue;
  }

  static company_employee_setvalue = new PermissionSet("ShardAdmin", ["Company.Employee.SetValue"]);

  Company_EmploymentRelationship_Add(): PermissionSet {
    return PermissionService.company_employmentrelationship_add;
  }

  static company_employmentrelationship_add = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.Add"]);

  Company_EmploymentRelationship_ChangeName(): PermissionSet {
    return PermissionService.company_employmentrelationship_changename;
  }

  static company_employmentrelationship_changename = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.ChangeName"]);

  Company_EmploymentRelationship_Delete(): PermissionSet {
    return PermissionService.company_employmentrelationship_delete;
  }

  static company_employmentrelationship_delete = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.Delete"]);

  Company_EmploymentRelationship_Get(): PermissionSet {
    return PermissionService.company_employmentrelationship_get;
  }

  static company_employmentrelationship_get = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.Get"]);

  Company_EmploymentRelationship_GetEventEmploymentCosts(): PermissionSet {
    return PermissionService.company_employmentrelationship_geteventemploymentcosts;
  }

  static company_employmentrelationship_geteventemploymentcosts = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.GetEventEmploymentCosts"]);

  Company_EmploymentRelationship_GetEventsEmploymentCosts(): PermissionSet {
    return PermissionService.company_employmentrelationship_geteventsemploymentcosts;
  }

  static company_employmentrelationship_geteventsemploymentcosts = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.GetEventsEmploymentCosts"]);

  Company_EmploymentRelationship_List(): PermissionSet {
    return PermissionService.company_employmentrelationship_list;
  }

  static company_employmentrelationship_list = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.List"]);

  Company_EmploymentRelationship_SetRelationship(): PermissionSet {
    return PermissionService.company_employmentrelationship_setrelationship;
  }

  static company_employmentrelationship_setrelationship = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.SetRelationship"]);

  Company_EmploymentRelationship_SetValues(): PermissionSet {
    return PermissionService.company_employmentrelationship_setvalues;
  }

  static company_employmentrelationship_setvalues = new PermissionSet("ShardAdmin", ["Company.EmploymentRelationship.SetValues"]);

  Company_Setting_List(): PermissionSet {
    return PermissionService.company_setting_list;
  }

  static company_setting_list = new PermissionSet("ShardAdmin", ["Company.Setting.List"]);

  Company_Setting_SetAuthPair(): PermissionSet {
    return PermissionService.company_setting_setauthpair;
  }

  static company_setting_setauthpair = new PermissionSet("ShardAdmin", ["Company.Setting.SetAuthPair"]);

  Company_Setting_SetAuthPairs(): PermissionSet {
    return PermissionService.company_setting_setauthpairs;
  }

  static company_setting_setauthpairs = new PermissionSet("ShardAdmin", ["Company.Setting.SetAuthPairs"]);

  Company_Setting_SumUpIsConfigured(): PermissionSet {
    return PermissionService.company_setting_sumupisconfigured;
  }

  static company_setting_sumupisconfigured = new PermissionSet("ShardAdmin", ["Company.Setting.SumUpIsConfigured"]);

  Company_Setting_TelegramIsConfigured(): PermissionSet {
    return PermissionService.company_setting_telegramisconfigured;
  }

  static company_setting_telegramisconfigured = new PermissionSet("ShardAdmin", ["Company.Setting.TelegramIsConfigured"]);

  Company_Supplier_Add(): PermissionSet {
    return PermissionService.company_supplier_add;
  }

  static company_supplier_add = new PermissionSet("ShardAdmin", ["Company.Supplier.Add"]);

  Company_Supplier_ChangeName(): PermissionSet {
    return PermissionService.company_supplier_changename;
  }

  static company_supplier_changename = new PermissionSet("ShardAdmin", ["Company.Supplier.ChangeName"]);

  Company_Supplier_Delete(): PermissionSet {
    return PermissionService.company_supplier_delete;
  }

  static company_supplier_delete = new PermissionSet("ShardAdmin", ["Company.Supplier.Delete"]);

  Company_Supplier_Get(): PermissionSet {
    return PermissionService.company_supplier_get;
  }

  static company_supplier_get = new PermissionSet("ShardAdmin", ["Company.Supplier.Get"]);

  Company_Supplier_List(): PermissionSet {
    return PermissionService.company_supplier_list;
  }

  static company_supplier_list = new PermissionSet("ShardAdmin", ["Company.Supplier.List"]);

  Company_Supplier_Update(): PermissionSet {
    return PermissionService.company_supplier_update;
  }

  static company_supplier_update = new PermissionSet("ShardAdmin", ["Company.Supplier.Update"]);

  Company_Telegram_AddBot(): PermissionSet {
    return PermissionService.company_telegram_addbot;
  }

  static company_telegram_addbot = new PermissionSet("ShardAdmin", ["Company.Telegram.AddBot"]);

  Company_Telegram_AddChannel(): PermissionSet {
    return PermissionService.company_telegram_addchannel;
  }

  static company_telegram_addchannel = new PermissionSet("ShardAdmin", ["Company.Telegram.AddChannel"]);

  Company_Telegram_DeleteBot(): PermissionSet {
    return PermissionService.company_telegram_deletebot;
  }

  static company_telegram_deletebot = new PermissionSet("ShardAdmin", ["Company.Telegram.DeleteBot"]);

  Company_Telegram_DeleteChannel(): PermissionSet {
    return PermissionService.company_telegram_deletechannel;
  }

  static company_telegram_deletechannel = new PermissionSet("ShardAdmin", ["Company.Telegram.DeleteChannel"]);

  Company_Telegram_GetBots(): PermissionSet {
    return PermissionService.company_telegram_getbots;
  }

  static company_telegram_getbots = new PermissionSet("ShardAdmin", ["Company.Telegram.GetBots"]);

  Company_Telegram_GetChannelSettings(): PermissionSet {
    return PermissionService.company_telegram_getchannelsettings;
  }

  static company_telegram_getchannelsettings = new PermissionSet("ShardAdmin", ["Company.Telegram.GetChannelSettings"]);

  Company_Telegram_List(): PermissionSet {
    return PermissionService.company_telegram_list;
  }

  static company_telegram_list = new PermissionSet("ShardAdmin", ["Company.Telegram.List"]);

  Company_Telegram_SetChannelSettings(): PermissionSet {
    return PermissionService.company_telegram_setchannelsettings;
  }

  static company_telegram_setchannelsettings = new PermissionSet("ShardAdmin", ["Company.Telegram.SetChannelSettings"]);

  Device_CashPointEntry_Close(): PermissionSet {
    return PermissionService.device_cashpointentry_close;
  }

  static device_cashpointentry_close = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.Close"]);

  Device_CashPointEntry_CurrentBusinessCases(): PermissionSet {
    return PermissionService.device_cashpointentry_currentbusinesscases;
  }

  static device_cashpointentry_currentbusinesscases = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.CurrentBusinessCases"]);

  Device_CashPointEntry_CurrentSummary(): PermissionSet {
    return PermissionService.device_cashpointentry_currentsummary;
  }

  static device_cashpointentry_currentsummary = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.CurrentSummary"]);

  Device_CashPointEntry_GetBusinessCaseTypeSummary(): PermissionSet {
    return PermissionService.device_cashpointentry_getbusinesscasetypesummary;
  }

  static device_cashpointentry_getbusinesscasetypesummary = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.GetBusinessCaseTypeSummary"]);

  Device_CashPointEntry_GetLastClosed(): PermissionSet {
    return PermissionService.device_cashpointentry_getlastclosed;
  }

  static device_cashpointentry_getlastclosed = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.GetLastClosed"]);

  Device_CashPointEntry_GetOpen(): PermissionSet {
    return PermissionService.device_cashpointentry_getopen;
  }

  static device_cashpointentry_getopen = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.GetOpen"]);

  Device_CashPointEntry_GetSummary(): PermissionSet {
    return PermissionService.device_cashpointentry_getsummary;
  }

  static device_cashpointentry_getsummary = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.GetSummary"]);

  Device_CashPointEntry_List(): PermissionSet {
    return PermissionService.device_cashpointentry_list;
  }

  static device_cashpointentry_list = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.List"]);

  Device_CashPointEntry_Open(): PermissionSet {
    return PermissionService.device_cashpointentry_open;
  }

  static device_cashpointentry_open = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.Open"]);

  Device_CashPointEntry_PrepareForClosing(): PermissionSet {
    return PermissionService.device_cashpointentry_prepareforclosing;
  }

  static device_cashpointentry_prepareforclosing = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.PrepareForClosing"]);

  Device_CashPointEntry_Summary(): PermissionSet {
    return PermissionService.device_cashpointentry_summary;
  }

  static device_cashpointentry_summary = new PermissionSet("ShardAdmin", ["Device.CashPointEntry.Summary"]);

  Device_Client_CheckOrderStatus(): PermissionSet {
    return PermissionService.device_client_checkorderstatus;
  }

  static device_client_checkorderstatus = new PermissionSet("ShardAdmin", ["Device.Client.CheckOrderStatus"]);

  Device_Client_GetData(): PermissionSet {
    return PermissionService.device_client_getdata;
  }

  static device_client_getdata = new PermissionSet("ShardAdmin", ["Device.Client.GetData"]);

  Device_Client_ProcessTransactions(): PermissionSet {
    return PermissionService.device_client_processtransactions;
  }

  static device_client_processtransactions = new PermissionSet("ShardAdmin", ["Device.Client.ProcessTransactions"]);

  Device_Information_ChangeName(): PermissionSet {
    return PermissionService.device_information_changename;
  }

  static device_information_changename = new PermissionSet("ShardAdmin", ["Device.Information.ChangeName"]);

  Device_Information_Delete(): PermissionSet {
    return PermissionService.device_information_delete;
  }

  static device_information_delete = new PermissionSet("ShardAdmin", ["Device.Information.Delete"]);

  Device_Information_Get(): PermissionSet {
    return PermissionService.device_information_get;
  }

  static device_information_get = new PermissionSet("ShardAdmin", ["Device.Information.Get"]);

  Device_Information_List(): PermissionSet {
    return PermissionService.device_information_list;
  }

  static device_information_list = new PermissionSet("ShardAdmin", ["Device.Information.List"]);

  Device_Information_RequestInformation(): PermissionSet {
    return PermissionService.device_information_requestinformation;
  }

  static device_information_requestinformation = new PermissionSet("ShardAdmin", ["Device.Information.RequestInformation"]);

  Device_Information_SetLockedOut(): PermissionSet {
    return PermissionService.device_information_setlockedout;
  }

  static device_information_setlockedout = new PermissionSet("ShardAdmin", ["Device.Information.SetLockedOut"]);

  Device_Printer_Add(): PermissionSet {
    return PermissionService.device_printer_add;
  }

  static device_printer_add = new PermissionSet("ShardAdmin", ["Device.Printer.Add"]);

  Device_Printer_ChangeName(): PermissionSet {
    return PermissionService.device_printer_changename;
  }

  static device_printer_changename = new PermissionSet("ShardAdmin", ["Device.Printer.ChangeName"]);

  Device_Printer_Delete(): PermissionSet {
    return PermissionService.device_printer_delete;
  }

  static device_printer_delete = new PermissionSet("ShardAdmin", ["Device.Printer.Delete"]);

  Device_Printer_Get(): PermissionSet {
    return PermissionService.device_printer_get;
  }

  static device_printer_get = new PermissionSet("ShardAdmin", ["Device.Printer.Get"]);

  Device_Printer_List(): PermissionSet {
    return PermissionService.device_printer_list;
  }

  static device_printer_list = new PermissionSet("ShardAdmin", ["Device.Printer.List"]);

  Device_Printer_SetVariable(): PermissionSet {
    return PermissionService.device_printer_setvariable;
  }

  static device_printer_setvariable = new PermissionSet("ShardAdmin", ["Device.Printer.SetVariable"]);

  Document_Employee_Download(): PermissionSet {
    return PermissionService.document_employee_download;
  }

  static document_employee_download = new PermissionSet("ShardAdmin", ["Document.Employee.Download"]);

  Document_Employee_DownloadIndividualDetails(): PermissionSet {
    return PermissionService.document_employee_downloadindividualdetails;
  }

  static document_employee_downloadindividualdetails = new PermissionSet("ShardAdmin", ["Document.Employee.DownloadIndividualDetails"]);

  Document_EmploymentRelationship_Download(): PermissionSet {
    return PermissionService.document_employmentrelationship_download;
  }

  static document_employmentrelationship_download = new PermissionSet("ShardAdmin", ["Document.EmploymentRelationship.Download"]);

  Document_ShiftMaker_Download(): PermissionSet {
    return PermissionService.document_shiftmaker_download;
  }

  static document_shiftmaker_download = new PermissionSet("ShardAdmin", ["Document.ShiftMaker.Download"]);

  Employee(): PermissionSet {
    return PermissionService.employee;
  }

  static employee = new PermissionSet("ShardAdmin", ["Employee"]);

  Hubs_AutomationHub_Subscribe(): PermissionSet {
    return PermissionService.hubs_automationhub_subscribe;
  }

  static hubs_automationhub_subscribe = new PermissionSet("ShardAdmin", ["Hubs.AutomationHub.Subscribe"]);

  Hubs_ClientHub_DV_Information_Set(): PermissionSet {
    return PermissionService.hubs_clienthub_dv_information_set;
  }

  static hubs_clienthub_dv_information_set = new PermissionSet("ShardAdmin", ["Hubs.ClientHub.DV_Information_Set"]);

  Hubs_ClientHub_DV_Location_Set(): PermissionSet {
    return PermissionService.hubs_clienthub_dv_location_set;
  }

  static hubs_clienthub_dv_location_set = new PermissionSet("ShardAdmin", ["Hubs.ClientHub.DV_Location_Set"]);

  Hubs_ClientHub_GetDeviceInformation(): PermissionSet {
    return PermissionService.hubs_clienthub_getdeviceinformation;
  }

  static hubs_clienthub_getdeviceinformation = new PermissionSet("ShardAdmin", ["Hubs.ClientHub.GetDeviceInformation"]);

  Hubs_ClientHub_O_Transmit(): PermissionSet {
    return PermissionService.hubs_clienthub_o_transmit;
  }

  static hubs_clienthub_o_transmit = new PermissionSet("ShardAdmin", ["Hubs.ClientHub.O_Transmit"]);

  Hubs_ClientHub_RequestHelp(): PermissionSet {
    return PermissionService.hubs_clienthub_requesthelp;
  }

  static hubs_clienthub_requesthelp = new PermissionSet("ShardAdmin", ["Hubs.ClientHub.RequestHelp"]);

  Hubs_DeviceHub_JoinListeners(): PermissionSet {
    return PermissionService.hubs_devicehub_joinlisteners;
  }

  static hubs_devicehub_joinlisteners = new PermissionSet("ShardAdmin", ["Hubs.DeviceHub.JoinListeners"]);

  Hubs_ShiftMakerHub_PushGeneralUpdate(): PermissionSet {
    return PermissionService.hubs_shiftmakerhub_pushgeneralupdate;
  }

  static hubs_shiftmakerhub_pushgeneralupdate = new PermissionSet("ShardAdmin", ["Hubs.ShiftMakerHub.PushGeneralUpdate"]);

  Hubs_ShiftMakerHub_PushLiveUpdate(): PermissionSet {
    return PermissionService.hubs_shiftmakerhub_pushliveupdate;
  }

  static hubs_shiftmakerhub_pushliveupdate = new PermissionSet("ShardAdmin", ["Hubs.ShiftMakerHub.PushLiveUpdate"]);

  Inventory_BusinessCase_Add(): PermissionSet {
    return PermissionService.inventory_businesscase_add;
  }

  static inventory_businesscase_add = new PermissionSet("ShardAdmin", ["Inventory.BusinessCase.Add"]);

  Inventory_BusinessCase_Delete(): PermissionSet {
    return PermissionService.inventory_businesscase_delete;
  }

  static inventory_businesscase_delete = new PermissionSet("ShardAdmin", ["Inventory.BusinessCase.Delete"]);

  Inventory_BusinessCase_Get(): PermissionSet {
    return PermissionService.inventory_businesscase_get;
  }

  static inventory_businesscase_get = new PermissionSet("ShardAdmin", ["Inventory.BusinessCase.Get"]);

  Inventory_BusinessCase_List(): PermissionSet {
    return PermissionService.inventory_businesscase_list;
  }

  static inventory_businesscase_list = new PermissionSet("ShardAdmin", ["Inventory.BusinessCase.List"]);

  Inventory_Category_Add(): PermissionSet {
    return PermissionService.inventory_category_add;
  }

  static inventory_category_add = new PermissionSet("ShardAdmin", ["Inventory.Category.Add"]);

  Inventory_Category_ChangeName(): PermissionSet {
    return PermissionService.inventory_category_changename;
  }

  static inventory_category_changename = new PermissionSet("ShardAdmin", ["Inventory.Category.ChangeName"]);

  Inventory_Category_Delete(): PermissionSet {
    return PermissionService.inventory_category_delete;
  }

  static inventory_category_delete = new PermissionSet("ShardAdmin", ["Inventory.Category.Delete"]);

  Inventory_Category_Get(): PermissionSet {
    return PermissionService.inventory_category_get;
  }

  static inventory_category_get = new PermissionSet("ShardAdmin", ["Inventory.Category.Get"]);

  Inventory_Category_List(): PermissionSet {
    return PermissionService.inventory_category_list;
  }

  static inventory_category_list = new PermissionSet("ShardAdmin", ["Inventory.Category.List"]);

  Inventory_Component_Add(): PermissionSet {
    return PermissionService.inventory_component_add;
  }

  static inventory_component_add = new PermissionSet("ShardAdmin", ["Inventory.Component.Add"]);

  Inventory_Component_ChangeName(): PermissionSet {
    return PermissionService.inventory_component_changename;
  }

  static inventory_component_changename = new PermissionSet("ShardAdmin", ["Inventory.Component.ChangeName"]);

  Inventory_Component_Delete(): PermissionSet {
    return PermissionService.inventory_component_delete;
  }

  static inventory_component_delete = new PermissionSet("ShardAdmin", ["Inventory.Component.Delete"]);

  Inventory_Component_Get(): PermissionSet {
    return PermissionService.inventory_component_get;
  }

  static inventory_component_get = new PermissionSet("ShardAdmin", ["Inventory.Component.Get"]);

  Inventory_Component_List(): PermissionSet {
    return PermissionService.inventory_component_list;
  }

  static inventory_component_list = new PermissionSet("ShardAdmin", ["Inventory.Component.List"]);

  Inventory_Composition_Add(): PermissionSet {
    return PermissionService.inventory_composition_add;
  }

  static inventory_composition_add = new PermissionSet("ShardAdmin", ["Inventory.Composition.Add"]);

  Inventory_Composition_ChangeName(): PermissionSet {
    return PermissionService.inventory_composition_changename;
  }

  static inventory_composition_changename = new PermissionSet("ShardAdmin", ["Inventory.Composition.ChangeName"]);

  Inventory_Composition_ChangePublicProperty(): PermissionSet {
    return PermissionService.inventory_composition_changepublicproperty;
  }

  static inventory_composition_changepublicproperty = new PermissionSet("ShardAdmin", ["Inventory.Composition.ChangePublicProperty"]);

  Inventory_Composition_ComponentChange(): PermissionSet {
    return PermissionService.inventory_composition_componentchange;
  }

  static inventory_composition_componentchange = new PermissionSet("ShardAdmin", ["Inventory.Composition.ComponentChange"]);

  Inventory_Composition_Create(): PermissionSet {
    return PermissionService.inventory_composition_create;
  }

  static inventory_composition_create = new PermissionSet("ShardAdmin", ["Inventory.Composition.Create"]);

  Inventory_Composition_Delete(): PermissionSet {
    return PermissionService.inventory_composition_delete;
  }

  static inventory_composition_delete = new PermissionSet("ShardAdmin", ["Inventory.Composition.Delete"]);

  Inventory_Composition_Download(): PermissionSet {
    return PermissionService.inventory_composition_download;
  }

  static inventory_composition_download = new PermissionSet("ShardAdmin", ["Inventory.Composition.Download"]);

  Inventory_Composition_Get(): PermissionSet {
    return PermissionService.inventory_composition_get;
  }

  static inventory_composition_get = new PermissionSet("ShardAdmin", ["Inventory.Composition.Get"]);

  Inventory_Composition_List(): PermissionSet {
    return PermissionService.inventory_composition_list;
  }

  static inventory_composition_list = new PermissionSet("ShardAdmin", ["Inventory.Composition.List"]);

  Inventory_Composition_PriceChange(): PermissionSet {
    return PermissionService.inventory_composition_pricechange;
  }

  static inventory_composition_pricechange = new PermissionSet("ShardAdmin", ["Inventory.Composition.PriceChange"]);

  Inventory_Composition_SetOrder(): PermissionSet {
    return PermissionService.inventory_composition_setorder;
  }

  static inventory_composition_setorder = new PermissionSet("ShardAdmin", ["Inventory.Composition.SetOrder"]);

  Inventory_Composition_ToggleTag(): PermissionSet {
    return PermissionService.inventory_composition_toggletag;
  }

  static inventory_composition_toggletag = new PermissionSet("ShardAdmin", ["Inventory.Composition.ToggleTag"]);

  Inventory_CompositionType_Add(): PermissionSet {
    return PermissionService.inventory_compositiontype_add;
  }

  static inventory_compositiontype_add = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.Add"]);

  Inventory_CompositionType_ChangeName(): PermissionSet {
    return PermissionService.inventory_compositiontype_changename;
  }

  static inventory_compositiontype_changename = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.ChangeName"]);

  Inventory_CompositionType_ChangeVat(): PermissionSet {
    return PermissionService.inventory_compositiontype_changevat;
  }

  static inventory_compositiontype_changevat = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.ChangeVat"]);

  Inventory_CompositionType_Delete(): PermissionSet {
    return PermissionService.inventory_compositiontype_delete;
  }

  static inventory_compositiontype_delete = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.Delete"]);

  Inventory_CompositionType_Get(): PermissionSet {
    return PermissionService.inventory_compositiontype_get;
  }

  static inventory_compositiontype_get = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.Get"]);

  Inventory_CompositionType_List(): PermissionSet {
    return PermissionService.inventory_compositiontype_list;
  }

  static inventory_compositiontype_list = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.List"]);

  Inventory_CompositionType_ListNl(): PermissionSet {
    return PermissionService.inventory_compositiontype_listnl;
  }

  static inventory_compositiontype_listnl = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.ListNl"]);

  Inventory_CompositionType_SetCompositionType(): PermissionSet {
    return PermissionService.inventory_compositiontype_setcompositiontype;
  }

  static inventory_compositiontype_setcompositiontype = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.SetCompositionType"]);

  Inventory_CompositionType_SetValue(): PermissionSet {
    return PermissionService.inventory_compositiontype_setvalue;
  }

  static inventory_compositiontype_setvalue = new PermissionSet("ShardAdmin", ["Inventory.CompositionType.SetValue"]);

  Inventory_Composition_Upsert(): PermissionSet {
    return PermissionService.inventory_composition_upsert;
  }

  static inventory_composition_upsert = new PermissionSet("ShardAdmin", ["Inventory.Composition.Upsert"]);

  Inventory_Configuration_Add(): PermissionSet {
    return PermissionService.inventory_configuration_add;
  }

  static inventory_configuration_add = new PermissionSet("ShardAdmin", ["Inventory.Configuration.Add"]);

  Inventory_ConfigurationBuilder_Add(): PermissionSet {
    return PermissionService.inventory_configurationbuilder_add;
  }

  static inventory_configurationbuilder_add = new PermissionSet("ShardAdmin", ["Inventory.ConfigurationBuilder.Add"]);

  Inventory_ConfigurationBuilder_List(): PermissionSet {
    return PermissionService.inventory_configurationbuilder_list;
  }

  static inventory_configurationbuilder_list = new PermissionSet("ShardAdmin", ["Inventory.ConfigurationBuilder.List"]);

  Inventory_ConfigurationBuilder_SetSpecial(): PermissionSet {
    return PermissionService.inventory_configurationbuilder_setspecial;
  }

  static inventory_configurationbuilder_setspecial = new PermissionSet("ShardAdmin", ["Inventory.ConfigurationBuilder.SetSpecial"]);

  Inventory_Configuration_ChangeName(): PermissionSet {
    return PermissionService.inventory_configuration_changename;
  }

  static inventory_configuration_changename = new PermissionSet("ShardAdmin", ["Inventory.Configuration.ChangeName"]);

  Inventory_Configuration_Delete(): PermissionSet {
    return PermissionService.inventory_configuration_delete;
  }

  static inventory_configuration_delete = new PermissionSet("ShardAdmin", ["Inventory.Configuration.Delete"]);

  Inventory_Configuration_Get(): PermissionSet {
    return PermissionService.inventory_configuration_get;
  }

  static inventory_configuration_get = new PermissionSet("ShardAdmin", ["Inventory.Configuration.Get"]);

  Inventory_Configuration_List(): PermissionSet {
    return PermissionService.inventory_configuration_list;
  }

  static inventory_configuration_list = new PermissionSet("ShardAdmin", ["Inventory.Configuration.List"]);

  Inventory_Event_Add(): PermissionSet {
    return PermissionService.inventory_event_add;
  }

  static inventory_event_add = new PermissionSet("ShardAdmin", ["Inventory.Event.Add"]);

  Inventory_Event_AddMany(): PermissionSet {
    return PermissionService.inventory_event_addmany;
  }

  static inventory_event_addmany = new PermissionSet("ShardAdmin", ["Inventory.Event.AddMany"]);

  Inventory_Event_ChangeName(): PermissionSet {
    return PermissionService.inventory_event_changename;
  }

  static inventory_event_changename = new PermissionSet("ShardAdmin", ["Inventory.Event.ChangeName"]);

  Inventory_Event_ChangePublicResponseTime(): PermissionSet {
    return PermissionService.inventory_event_changepublicresponsetime;
  }

  static inventory_event_changepublicresponsetime = new PermissionSet("ShardAdmin", ["Inventory.Event.ChangePublicResponseTime"]);

  Inventory_Event_ChangeTime(): PermissionSet {
    return PermissionService.inventory_event_changetime;
  }

  static inventory_event_changetime = new PermissionSet("ShardAdmin", ["Inventory.Event.ChangeTime"]);

  Inventory_Event_Delete(): PermissionSet {
    return PermissionService.inventory_event_delete;
  }

  static inventory_event_delete = new PermissionSet("ShardAdmin", ["Inventory.Event.Delete"]);

  Inventory_Event_Download(): PermissionSet {
    return PermissionService.inventory_event_download;
  }

  static inventory_event_download = new PermissionSet("ShardAdmin", ["Inventory.Event.Download"]);

  Inventory_Event_Downloads(): PermissionSet {
    return PermissionService.inventory_event_downloads;
  }

  static inventory_event_downloads = new PermissionSet("ShardAdmin", ["Inventory.Event.Downloads"]);

  Inventory_Event_Get(): PermissionSet {
    return PermissionService.inventory_event_get;
  }

  static inventory_event_get = new PermissionSet("ShardAdmin", ["Inventory.Event.Get"]);

  /**
   * Reading the creation allowance (#664) — "what kind of event may I create in this window?".
   *
   * Gated exactly like {@link Inventory_Event_Add}: the endpoint runs the same contract gate the create call
   * runs, so anybody who may open the creation form may ask it, and nobody else can probe a company's
   * contract limits through it.
   */
  Inventory_Event_GetCreationAllowance(): PermissionSet {
    return PermissionService.inventory_event_getcreationallowance;
  }

  static inventory_event_getcreationallowance = new PermissionSet("ShardAdmin", ["Inventory.Event.GetCreationAllowance"]);

  Inventory_Event_GetCurrentOrNext(): PermissionSet {
    return PermissionService.inventory_event_getcurrentornext;
  }

  static inventory_event_getcurrentornext = new PermissionSet("ShardAdmin", ["Inventory.Event.GetCurrentOrNext"]);

  Inventory_Event_GetDisabledLocations(): PermissionSet {
    return PermissionService.inventory_event_getdisabledlocations;
  }

  static inventory_event_getdisabledlocations = new PermissionSet("ShardAdmin", ["Inventory.Event.GetDisabledLocations"]);

  Inventory_Event_GetEmployeeAvailability(): PermissionSet {
    return PermissionService.inventory_event_getemployeeavailability;
  }

  static inventory_event_getemployeeavailability = new PermissionSet("ShardAdmin", ["Inventory.Event.GetEmployeeAvailability"]);

  Inventory_Event_GetEmployeeOrders(): PermissionSet {
    return PermissionService.inventory_event_getemployeeorders;
  }

  static inventory_event_getemployeeorders = new PermissionSet("ShardAdmin", ["Inventory.Event.GetEmployeeOrders"]);

  Inventory_Event_GetEventSummary(): PermissionSet {
    return PermissionService.inventory_event_geteventsummary;
  }

  static inventory_event_geteventsummary = new PermissionSet("ShardAdmin", ["Inventory.Event.GetEventSummary"]);

  Inventory_Event_GetExtendedEvent(): PermissionSet {
    return PermissionService.inventory_event_getextendedevent;
  }

  static inventory_event_getextendedevent = new PermissionSet("ShardAdmin", ["Inventory.Event.GetExtendedEvent"]);

  Inventory_Event_GetPrivatePublicEvents(): PermissionSet {
    return PermissionService.inventory_event_getprivatepublicevents;
  }

  static inventory_event_getprivatepublicevents = new PermissionSet("ShardAdmin", ["Inventory.Event.GetPrivatePublicEvents"]);

  Inventory_Event_GetPublicEvents(): PermissionSet {
    return PermissionService.inventory_event_getpublicevents;
  }

  static inventory_event_getpublicevents = new PermissionSet("ShardAdmin", ["Inventory.Event.GetPublicEvents"]);

  Inventory_Event_List(): PermissionSet {
    return PermissionService.inventory_event_list;
  }

  static inventory_event_list = new PermissionSet("ShardAdmin", ["Inventory.Event.List"]);

  Inventory_Event_SendEmployeeEventResponse(): PermissionSet {
    return PermissionService.inventory_event_sendemployeeeventresponse;
  }

  static inventory_event_sendemployeeeventresponse = new PermissionSet("ShardAdmin", ["Inventory.Event.SendEmployeeEventResponse"]);

  Inventory_Event_SetLocationConfigs(): PermissionSet {
    return PermissionService.inventory_event_setlocationconfigs;
  }

  static inventory_event_setlocationconfigs = new PermissionSet("ShardAdmin", ["Inventory.Event.SetLocationConfigs"]);

  Inventory_Event_SetPublicVariable(): PermissionSet {
    return PermissionService.inventory_event_setpublicvariable;
  }

  static inventory_event_setpublicvariable = new PermissionSet("ShardAdmin", ["Inventory.Event.SetPublicVariable"]);

  Inventory_Event_Upsert(): PermissionSet {
    return PermissionService.inventory_event_upsert;
  }

  static inventory_event_upsert = new PermissionSet("ShardAdmin", ["Inventory.Event.Upsert"]);

  Inventory_General_GetDashboardStats(): PermissionSet {
    return PermissionService.inventory_general_getdashboardstats;
  }

  static inventory_general_getdashboardstats = new PermissionSet("ShardAdmin", ["Inventory.General.GetDashboardStats"]);

  Inventory_General_OnlineCheck(): PermissionSet {
    return PermissionService.inventory_general_onlinecheck;
  }

  static inventory_general_onlinecheck = new PermissionSet("ShardAdmin", ["Inventory.General.OnlineCheck"]);

  Inventory_InterchangeableComponent_List(): PermissionSet {
    return PermissionService.inventory_interchangeablecomponent_list;
  }

  static inventory_interchangeablecomponent_list = new PermissionSet("ShardAdmin", ["Inventory.InterchangeableComponent.List"]);

  Inventory_Item_Add(): PermissionSet {
    return PermissionService.inventory_item_add;
  }

  static inventory_item_add = new PermissionSet("ShardAdmin", ["Inventory.Item.Add"]);

  Inventory_Item_ChangeName(): PermissionSet {
    return PermissionService.inventory_item_changename;
  }

  static inventory_item_changename = new PermissionSet("ShardAdmin", ["Inventory.Item.ChangeName"]);

  Inventory_Item_Delete(): PermissionSet {
    return PermissionService.inventory_item_delete;
  }

  static inventory_item_delete = new PermissionSet("ShardAdmin", ["Inventory.Item.Delete"]);

  Inventory_Item_Get(): PermissionSet {
    return PermissionService.inventory_item_get;
  }

  static inventory_item_get = new PermissionSet("ShardAdmin", ["Inventory.Item.Get"]);

  Inventory_Item_GetEventSourcingCosts(): PermissionSet {
    return PermissionService.inventory_item_geteventsourcingcosts;
  }

  static inventory_item_geteventsourcingcosts = new PermissionSet("ShardAdmin", ["Inventory.Item.GetEventSourcingCosts"]);

  Inventory_Item_GetEventsSourcingCosts(): PermissionSet {
    return PermissionService.inventory_item_geteventssourcingcosts;
  }

  static inventory_item_geteventssourcingcosts = new PermissionSet("ShardAdmin", ["Inventory.Item.GetEventsSourcingCosts"]);

  Inventory_Item_List(): PermissionSet {
    return PermissionService.inventory_item_list;
  }

  static inventory_item_list = new PermissionSet("ShardAdmin", ["Inventory.Item.List"]);

  Inventory_Item_SetSourceDetail(): PermissionSet {
    return PermissionService.inventory_item_setsourcedetail;
  }

  static inventory_item_setsourcedetail = new PermissionSet("ShardAdmin", ["Inventory.Item.SetSourceDetail"]);

  Inventory_Item_SetUnit(): PermissionSet {
    return PermissionService.inventory_item_setunit;
  }

  static inventory_item_setunit = new PermissionSet("ShardAdmin", ["Inventory.Item.SetUnit"]);

  Inventory_Location_Add(): PermissionSet {
    return PermissionService.inventory_location_add;
  }

  static inventory_location_add = new PermissionSet("ShardAdmin", ["Inventory.Location.Add"]);

  Inventory_Location_ChangeName(): PermissionSet {
    return PermissionService.inventory_location_changename;
  }

  static inventory_location_changename = new PermissionSet("ShardAdmin", ["Inventory.Location.ChangeName"]);

  Inventory_Location_ClientList(): PermissionSet {
    return PermissionService.inventory_location_clientlist;
  }

  static inventory_location_clientlist = new PermissionSet("ShardAdmin", ["Inventory.Location.ClientList"]);

  Inventory_Location_Delete(): PermissionSet {
    return PermissionService.inventory_location_delete;
  }

  static inventory_location_delete = new PermissionSet("ShardAdmin", ["Inventory.Location.Delete"]);

  Inventory_Location_Get(): PermissionSet {
    return PermissionService.inventory_location_get;
  }

  static inventory_location_get = new PermissionSet("ShardAdmin", ["Inventory.Location.Get"]);

  Inventory_Location_GetCompanyLocations(): PermissionSet {
    return PermissionService.inventory_location_getcompanylocations;
  }

  static inventory_location_getcompanylocations = new PermissionSet("ShardAdmin", ["Inventory.Location.GetCompanyLocations"]);

  Inventory_Location_List(): PermissionSet {
    return PermissionService.inventory_location_list;
  }

  static inventory_location_list = new PermissionSet("ShardAdmin", ["Inventory.Location.List"]);

  Inventory_Location_PushLocation(): PermissionSet {
    return PermissionService.inventory_location_pushlocation;
  }

  static inventory_location_pushlocation = new PermissionSet("ShardAdmin", ["Inventory.Location.PushLocation"]);

  Inventory_Location_SetDefaultEmployeeCount(): PermissionSet {
    return PermissionService.inventory_location_setdefaultemployeecount;
  }

  static inventory_location_setdefaultemployeecount = new PermissionSet("ShardAdmin", ["Inventory.Location.SetDefaultEmployeeCount"]);

  Inventory_Location_SetLocationConfig(): PermissionSet {
    return PermissionService.inventory_location_setlocationconfig;
  }

  static inventory_location_setlocationconfig = new PermissionSet("ShardAdmin", ["Inventory.Location.SetLocationConfig"]);

  Inventory_Location_SetOrder(): PermissionSet {
    return PermissionService.inventory_location_setorder;
  }

  static inventory_location_setorder = new PermissionSet("ShardAdmin", ["Inventory.Location.SetOrder"]);

  Inventory_Location_SetSalaryModifier(): PermissionSet {
    return PermissionService.inventory_location_setsalarymodifier;
  }

  static inventory_location_setsalarymodifier = new PermissionSet("ShardAdmin", ["Inventory.Location.SetSalaryModifier"]);

  Inventory_Location_SetTipShare(): PermissionSet {
    return PermissionService.inventory_location_settipshare;
  }

  static inventory_location_settipshare = new PermissionSet("ShardAdmin", ["Inventory.Location.SetTipShare"]);

  Inventory_Location_SetValue(): PermissionSet {
    return PermissionService.inventory_location_setvalue;
  }

  static inventory_location_setvalue = new PermissionSet("ShardAdmin", ["Inventory.Location.SetValue"]);

  Inventory_Override_Composition(): PermissionSet {
    return PermissionService.inventory_override_composition;
  }

  static inventory_override_composition = new PermissionSet("ShardAdmin", ["Inventory.Override.Composition"]);

  Inventory_Override_Tag(): PermissionSet {
    return PermissionService.inventory_override_tag;
  }

  static inventory_override_tag = new PermissionSet("ShardAdmin", ["Inventory.Override.Tag"]);

  Inventory_Tag_Add(): PermissionSet {
    return PermissionService.inventory_tag_add;
  }

  static inventory_tag_add = new PermissionSet("ShardAdmin", ["Inventory.Tag.Add"]);

  Inventory_Tag_ChangeColor(): PermissionSet {
    return PermissionService.inventory_tag_changecolor;
  }

  static inventory_tag_changecolor = new PermissionSet("ShardAdmin", ["Inventory.Tag.ChangeColor"]);

  Inventory_Tag_ChangeName(): PermissionSet {
    return PermissionService.inventory_tag_changename;
  }

  static inventory_tag_changename = new PermissionSet("ShardAdmin", ["Inventory.Tag.ChangeName"]);

  Inventory_Tag_Delete(): PermissionSet {
    return PermissionService.inventory_tag_delete;
  }

  static inventory_tag_delete = new PermissionSet("ShardAdmin", ["Inventory.Tag.Delete"]);

  Inventory_Tag_Get(): PermissionSet {
    return PermissionService.inventory_tag_get;
  }

  static inventory_tag_get = new PermissionSet("ShardAdmin", ["Inventory.Tag.Get"]);

  Inventory_Tag_List(): PermissionSet {
    return PermissionService.inventory_tag_list;
  }

  static inventory_tag_list = new PermissionSet("ShardAdmin", ["Inventory.Tag.List"]);

  Inventory_Tag_SetFilter(): PermissionSet {
    return PermissionService.inventory_tag_setfilter;
  }

  static inventory_tag_setfilter = new PermissionSet("ShardAdmin", ["Inventory.Tag.SetFilter"]);

  Inventory_Tag_SetOrder(): PermissionSet {
    return PermissionService.inventory_tag_setorder;
  }

  static inventory_tag_setorder = new PermissionSet("ShardAdmin", ["Inventory.Tag.SetOrder"]);

  Legal_Settings_IsConfigured(): PermissionSet {
    return PermissionService.legal_settings_isconfigured;
  }

  static legal_settings_isconfigured = new PermissionSet("ShardAdmin", ["Legal.Settings.IsConfigured"]);

  Legal_Settings_List(): PermissionSet {
    return PermissionService.legal_settings_list;
  }

  static legal_settings_list = new PermissionSet("ShardAdmin", ["Legal.Settings.List"]);

  /**
   * (#613) Selecting a legal provider is now **PlatformOwner-only** — `Settings.SelectProvider` moved behind
   * `[DynamicAuthorize("Legal", MetaRole.Owner)]`, so a `ShardAdmin` session now gets a 403 from the endpoint
   * this set used to admit it to. `default` repeats the central role rather than the retired legacy id, for the
   * same reason {@link owner} does: `CheckPermission` ends in `default.every(...)`, and the legacy
   * `"Legal.Settings.SelectProvider"` id no longer gates anything server-side, so keeping it here would have
   * looked like a requirement while granting nothing. ShardAdmin still reaches the *page* (unchanged
   * {@link Permissions_LegalSettingsComponent}) — it renders read-only there via the `legal-readonly-hint`.
   */
  Legal_Settings_SelectProvider(): PermissionSet {
    return PermissionService.legal_settings_selectprovider;
  }

  static legal_settings_selectprovider = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  /** (#613) Same change as {@link legal_settings_selectprovider} — `Settings.SetCredentials` is owner-only. */
  Legal_Settings_SetCredentials(): PermissionSet {
    return PermissionService.legal_settings_setcredentials;
  }

  static legal_settings_setcredentials = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  /** (#613) Same change — every TSS state mutation (`TSS.ChangeState`) is owner-only; `TSS.GetCurrent` stays read. */
  Legal_TSS_ChangeState(): PermissionSet {
    return PermissionService.legal_tss_changestate;
  }

  static legal_tss_changestate = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  /** (#613) Same change — `TSS.Create` is owner-only. */
  Legal_TSS_Create(): PermissionSet {
    return PermissionService.legal_tss_create;
  }

  static legal_tss_create = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Legal_TSS_GetCurrent(): PermissionSet {
    return PermissionService.legal_tss_getcurrent;
  }

  static legal_tss_getcurrent = new PermissionSet("ShardAdmin", ["Legal.TSS.GetCurrent"]);

  /** (#613) `GET /legal/TSS/History` — a read, so it stays with the rest of the page's ShardAdmin+ reads. */
  Legal_TSS_History(): PermissionSet {
    return PermissionService.legal_tss_history;
  }

  static legal_tss_history = new PermissionSet("ShardAdmin", ["Legal.TSS.GetCurrent"]);

  /**
   * (#613) `POST /legal/TSS/ReInitialize` — retires the shard's TSE and mints a fresh one; PlatformOwner only,
   * same shape as {@link legal_settings_selectprovider}.
   */
  Legal_TSS_ReInitialize(): PermissionSet {
    return PermissionService.legal_tss_reinitialize;
  }

  static legal_tss_reinitialize = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  /*
   * REMOVED (#769): `Administration_Settings_GetSupportContact()` / `static
   * administration_settings_getsupportcontact`.
   *
   * The endpoint it described moved to `GET /miscellaneous/Support/GetSupportContact` and now carries a plain
   * `[Authorize]` with no role at all (`API/Controller/Miscellaneous/SupportController.cs`), so there is no
   * gate left for a PermissionSet to mirror. Nothing ever called it — `SupportContactService` issues the
   * request unconditionally and fails soft — so this was already a description of the backend rather than a
   * check anybody performed, and a description that had drifted: it claimed `ShardAdmin/CompanyOwner+` while
   * the controller was really behind `[DynamicAuthorize("Administration", MetaRole.Admin, CompanyOwner)]`,
   * which is what silently refused a `Steuerberater` the address on the TSE page.
   *
   * Deleted rather than repointed: a set that gates nothing is the trap `GetDeactivatedPermission()` was (see
   * the note at the top of this class). If a surface ever needs to ask "may this session read the support
   * address", the honest answer is "yes, if it is signed in", which needs no set.
   */

  Mailing_Submission_Submit(): PermissionSet {
    return PermissionService.mailing_submission_submit;
  }

  static mailing_submission_submit = new PermissionSet("ShardAdmin", ["Mailing.Submission.Submit"]);

  Management_Changelog_Add(): PermissionSet {
    return PermissionService.management_changelog_add;
  }

  static management_changelog_add = new PermissionSet("ShardAdmin", ["Management.Changelog.Add"]);

  Management_Changelog_Delete(): PermissionSet {
    return PermissionService.management_changelog_delete;
  }

  static management_changelog_delete = new PermissionSet("ShardAdmin", ["Management.Changelog.Delete"]);

  Management_Changelog_Reactivate(): PermissionSet {
    return PermissionService.management_changelog_reactivate;
  }

  static management_changelog_reactivate = new PermissionSet("ShardAdmin", ["Management.Changelog.Reactivate"]);

  Management_Changelog_Update(): PermissionSet {
    return PermissionService.management_changelog_update;
  }

  static management_changelog_update = new PermissionSet("ShardAdmin", ["Management.Changelog.Update"]);

  Management_Company_ChangeName(): PermissionSet {
    return PermissionService.management_company_changename;
  }

  static management_company_changename = new PermissionSet("ShardAdmin", ["Management.Company.ChangeName"]);

  Management_Company_Delete(): PermissionSet {
    return PermissionService.management_company_delete;
  }

  static management_company_delete = new PermissionSet("ShardAdmin", ["Management.Company.Delete"]);

  Management_Company_Get(): PermissionSet {
    return PermissionService.management_company_get;
  }

  static management_company_get = new PermissionSet("ShardAdmin", ["Management.Company.Get"]);

  Management_Company_GetCompanies(): PermissionSet {
    return PermissionService.management_company_getcompanies;
  }

  static management_company_getcompanies = new PermissionSet("ShardAdmin", ["Management.Company.GetCompanies"]);

  Management_Company_GetCompany(): PermissionSet {
    return PermissionService.management_company_getcompany;
  }

  static management_company_getcompany = new PermissionSet("ShardAdmin", ["Management.Company.GetCompany"]);

  Management_Company_List(): PermissionSet {
    return PermissionService.management_company_list;
  }

  static management_company_list = new PermissionSet("ShardAdmin", ["Management.Company.List"]);

  Management_Company_SetAddress(): PermissionSet {
    return PermissionService.management_company_setaddress;
  }

  static management_company_setaddress = new PermissionSet("ShardAdmin", ["Management.Company.SetAddress"]);

  Management_Company_SetAlias(): PermissionSet {
    return PermissionService.management_company_setalias;
  }

  static management_company_setalias = new PermissionSet("ShardAdmin", ["Management.Company.SetAlias"]);

  Management_Company_SetMetadata(): PermissionSet {
    return PermissionService.management_company_setmetadata;
  }

  static management_company_setmetadata = new PermissionSet("ShardAdmin", ["Management.Company.SetMetadata"]);

  Management_Company_Upsert(): PermissionSet {
    return PermissionService.management_company_upsert;
  }

  static management_company_upsert = new PermissionSet("ShardAdmin", ["Management.Company.Upsert"]);

  Management_Contract_Delete(): PermissionSet {
    return PermissionService.management_contract_delete;
  }

  static management_contract_delete = new PermissionSet("ShardAdmin", ["Management.Contract.Delete"]);

  Management_Contract_GetCompany(): PermissionSet {
    return PermissionService.management_contract_getcompany;
  }

  static management_contract_getcompany = new PermissionSet("ShardAdmin", ["Management.Contract.GetCompany"]);

  Management_Contract_GetCustomerValues(): PermissionSet {
    return PermissionService.management_contract_getcustomervalues;
  }

  static management_contract_getcustomervalues = new PermissionSet("ShardAdmin", ["Management.Contract.GetCustomerValues"]);

  Management_Contract_Set(): PermissionSet {
    return PermissionService.management_contract_set;
  }

  static management_contract_set = new PermissionSet("ShardAdmin", ["Management.Contract.Set"]);

  Management_Contract_Update(): PermissionSet {
    return PermissionService.management_contract_update;
  }

  static management_contract_update = new PermissionSet("ShardAdmin", ["Management.Contract.Update"]);

  Management_Device_Location(): PermissionSet {
    return PermissionService.management_device_location;
  }

  static management_device_location = new PermissionSet("ShardAdmin", ["Management.Device.Location"]);

  Management_Environment_Add(): PermissionSet {
    return PermissionService.management_environment_add;
  }

  static management_environment_add = new PermissionSet("ShardAdmin", ["Management.Environment.Add"]);

  Management_Environment_Delete(): PermissionSet {
    return PermissionService.management_environment_delete;
  }

  static management_environment_delete = new PermissionSet("ShardAdmin", ["Management.Environment.Delete"]);

  Management_Environment_Get(): PermissionSet {
    return PermissionService.management_environment_get;
  }

  static management_environment_get = new PermissionSet("ShardAdmin", ["Management.Environment.Get"]);

  Management_Environment_List(): PermissionSet {
    return PermissionService.management_environment_list;
  }

  static management_environment_list = new PermissionSet("ShardAdmin", ["Management.Environment.List"]);

  Management_Environment_Upsert(): PermissionSet {
    return PermissionService.management_environment_upsert;
  }

  static management_environment_upsert = new PermissionSet("ShardAdmin", ["Management.Environment.Upsert"]);

  Management_Fiskaly_IsConfigured(): PermissionSet {
    return PermissionService.management_fiskaly_isconfigured;
  }

  static management_fiskaly_isconfigured = new PermissionSet("ShardAdmin", ["Management.Fiskaly.IsConfigured"]);

  Management_Fiskaly_SetCredentials(): PermissionSet {
    return PermissionService.management_fiskaly_setcredentials;
  }

  static management_fiskaly_setcredentials = new PermissionSet("ShardAdmin", ["Management.Fiskaly.SetCredentials"]);

  Management_Fiskaly_UpsertShard(): PermissionSet {
    return PermissionService.management_fiskaly_upsertshard;
  }

  static management_fiskaly_upsertshard = new PermissionSet("ShardAdmin", ["Management.Fiskaly.UpsertShard"]);

  Management_Hangfire_Authorize(): PermissionSet {
    return PermissionService.management_hangfire_authorize;
  }

  static management_hangfire_authorize = new PermissionSet("ShardAdmin", ["Management.Hangfire.Authorize"]);

  /**
   * (#633) The owner-area, shard-scoped twin of {@link legal_settings_isconfigured} — same PlatformOwner-only
   * shape as {@link legal_settings_selectprovider}, for the same reason: management routes carry no legacy
   * endpoint-permission id, so `default` repeats the central role rather than an id that gates nothing.
   */
  Management_Legal_Settings_IsConfigured(): PermissionSet {
    return PermissionService.management_legal_settings_isconfigured;
  }

  static management_legal_settings_isconfigured = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_Settings_List(): PermissionSet {
    return PermissionService.management_legal_settings_list;
  }

  static management_legal_settings_list = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_Settings_SelectProvider(): PermissionSet {
    return PermissionService.management_legal_settings_selectprovider;
  }

  static management_legal_settings_selectprovider = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_Settings_SetCredentials(): PermissionSet {
    return PermissionService.management_legal_settings_setcredentials;
  }

  static management_legal_settings_setcredentials = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_TSS_ChangeState(): PermissionSet {
    return PermissionService.management_legal_tss_changestate;
  }

  static management_legal_tss_changestate = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_TSS_Create(): PermissionSet {
    return PermissionService.management_legal_tss_create;
  }

  static management_legal_tss_create = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_TSS_GetCurrent(): PermissionSet {
    return PermissionService.management_legal_tss_getcurrent;
  }

  static management_legal_tss_getcurrent = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_TSS_History(): PermissionSet {
    return PermissionService.management_legal_tss_history;
  }

  static management_legal_tss_history = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Legal_TSS_ReInitialize(): PermissionSet {
    return PermissionService.management_legal_tss_reinitialize;
  }

  static management_legal_tss_reinitialize = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Management_Permission_GenerateTsEndpoints(): PermissionSet {
    return PermissionService.management_permission_generatetsendpoints;
  }

  static management_permission_generatetsendpoints = new PermissionSet("ShardAdmin", ["Management.Permission.GenerateTsEndpoints"]);

  Management_Permission_GenerateTsPermissionSets(): PermissionSet {
    return PermissionService.management_permission_generatetspermissionsets;
  }

  static management_permission_generatetspermissionsets = new PermissionSet("ShardAdmin", ["Management.Permission.GenerateTsPermissionSets"]);

  Management_Setup_IsAlive(): PermissionSet {
    return PermissionService.management_setup_isalive;
  }

  static management_setup_isalive = new PermissionSet("ShardAdmin", ["Management.Setup.IsAlive"]);

  Management_Shard_Delete(): PermissionSet {
    return PermissionService.management_shard_delete;
  }

  static management_shard_delete = new PermissionSet("ShardAdmin", ["Management.Shard.Delete"]);

  Management_Shard_Get(): PermissionSet {
    return PermissionService.management_shard_get;
  }

  static management_shard_get = new PermissionSet("ShardAdmin", ["Management.Shard.Get"]);

  Management_Shard_GetShard(): PermissionSet {
    return PermissionService.management_shard_getshard;
  }

  static management_shard_getshard = new PermissionSet("ShardAdmin", ["Management.Shard.GetShard"]);

  Management_Shard_GetShards(): PermissionSet {
    return PermissionService.management_shard_getshards;
  }

  static management_shard_getshards = new PermissionSet("ShardAdmin", ["Management.Shard.GetShards"]);

  Management_Shard_List(): PermissionSet {
    return PermissionService.management_shard_list;
  }

  static management_shard_list = new PermissionSet("ShardAdmin", ["Management.Shard.List"]);

  Management_Shard_SetMetadata(): PermissionSet {
    return PermissionService.management_shard_setmetadata;
  }

  static management_shard_setmetadata = new PermissionSet("ShardAdmin", ["Management.Shard.SetMetadata"]);

  Management_Shard_Update(): PermissionSet {
    return PermissionService.management_shard_update;
  }

  static management_shard_update = new PermissionSet("ShardAdmin", ["Management.Shard.Update"]);

  Management_Shard_Upsert(): PermissionSet {
    return PermissionService.management_shard_upsert;
  }

  static management_shard_upsert = new PermissionSet("ShardAdmin", ["Management.Shard.Upsert"]);

  Management_User_Create(): PermissionSet {
    return PermissionService.management_user_create;
  }

  static management_user_create = new PermissionSet("ShardAdmin", ["Management.User.Create"]);

  Management_User_Delete(): PermissionSet {
    return PermissionService.management_user_delete;
  }

  static management_user_delete = new PermissionSet("ShardAdmin", ["Management.User.Delete"]);

  Management_User_List(): PermissionSet {
    return PermissionService.management_user_list;
  }

  static management_user_list = new PermissionSet("ShardAdmin", ["Management.User.List"]);

  Miscellaneous_Changelog_List(): PermissionSet {
    return PermissionService.miscellaneous_changelog_list;
  }

  static miscellaneous_changelog_list = new PermissionSet("ShardAdmin", ["Miscellaneous.Changelog.List"]);

  Operator(): PermissionSet {
    return PermissionService.operator;
  }

  static operator = new PermissionSet("ShardAdmin", ["Operator"]);

  Owner(): PermissionSet {
    return PermissionService.owner;
  }

  /**
   * "This is for the platform owner." The `_super` is `PlatformOwner`, not the `"ShardAdmin"` every other set in
   * this generated block carries: there, `_super` is the client-side mirror of the `Admin` meta role superseding
   * the legacy granular ids. Here `default` is itself a **central role**, so a `ShardAdmin` `_super` did not
   * widen the gate — it inverted it. `CheckPermission` short-circuits on `_super` before it reads `default`, so
   * every shard admin passed the one set written to keep them out. Pinned in `permission.service.spec.ts`.
   */
  static owner = new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner]);

  Payment_Settings_BookingReferenceType(): PermissionSet {
    return PermissionService.payment_settings_bookingreferencetype;
  }

  static payment_settings_bookingreferencetype = new PermissionSet("ShardAdmin", ["Payment.Settings.BookingReferenceType"]);

  Payment_Settings_Get(): PermissionSet {
    return PermissionService.payment_settings_get;
  }

  static payment_settings_get = new PermissionSet("ShardAdmin", ["Payment.Settings.Get"]);

  Payment_Settings_IsConfigured(): PermissionSet {
    return PermissionService.payment_settings_isconfigured;
  }

  static payment_settings_isconfigured = new PermissionSet("ShardAdmin", ["Payment.Settings.IsConfigured"]);

  Payment_Settings_SelectProvider(): PermissionSet {
    return PermissionService.payment_settings_selectprovider;
  }

  static payment_settings_selectprovider = new PermissionSet("ShardAdmin", ["Payment.Settings.SelectProvider"]);

  Payment_Settings_SetCredentials(): PermissionSet {
    return PermissionService.payment_settings_setcredentials;
  }

  static payment_settings_setcredentials = new PermissionSet("ShardAdmin", ["Payment.Settings.SetCredentials"]);

  Register(): PermissionSet {
    return PermissionService.register;
  }

  static register = new PermissionSet("ShardAdmin", ["Register"]);

  Tokens_BasicToken_IsTokenExisting(): PermissionSet {
    return PermissionService.tokens_basictoken_istokenexisting;
  }

  static tokens_basictoken_istokenexisting = new PermissionSet("ShardAdmin", ["Tokens.BasicToken.IsTokenExisting"]);

  Tokens_BasicToken_SendConfirmationCode(): PermissionSet {
    return PermissionService.tokens_basictoken_sendconfirmationcode;
  }

  static tokens_basictoken_sendconfirmationcode = new PermissionSet("ShardAdmin", ["Tokens.BasicToken.SendConfirmationCode"]);

  /* copy end */

  /**
   * Central roles that widen a set from the **generated** block above.
   *
   * The block between `copy start` / `copy end` is pasted from
   * `API.Services…PermissionService.GenerateTypescriptPermissions()`, one set per legacy endpoint permission —
   * it knows nothing about central roles, and a regeneration would silently drop anything edited into it. The
   * widenings therefore live here, in the hand-maintained region, where they also read as one list of
   * "generated set ← central role" rather than being buried among ~900 generated lines.
   *
   * Each entry mirrors a backend `[Authorize]` list that already names the role, so this narrows nothing and
   * weakens nothing — it stops the SPA from hiding a surface the server would happily serve:
   *
   * - `Document.ShiftMaker.Download` ← `ShiftMakerController.Download` (#280), which additionally bounds a
   *   `ShiftLead` to the running event's window via `IShiftLeadGuard`. That window check stays server-side.
   * - `Hubs.ShiftMakerHub.PushLiveUpdate` ← `ShiftMakerHub.PushLiveUpdate` (#280). Without it a shift lead
   *   opens the plan read-only *and* `ShiftMakerComponent.ngOnInit` picks neither push endpoint, so its
   *   `endpoint` stays empty and every edit goes nowhere. `ShiftMakerComponent.canEdit` already ANDs the live
   *   event window client-side, which is stricter than the server's window-plus-grace.
   *
   * `Hubs.ShiftMakerHub.PushGeneralUpdate` is deliberately NOT widened: it is the planner's unrestricted
   * everyday endpoint, and a shift lead's authority is the live event only.
   *
   * - `Legal.TSS.GetCurrent` and `Legal.TSS.History` ← `TSSController.GetCurrent`/`History` (#769), which
   *   restate their gate as `[DynamicAuthorize("Legal", MetaRole.Admin, Roles.Steuerberater)]`. The three
   *   remaining legal reads (`Legal.Settings.List`, `Legal.Settings.IsConfigured`,
   *   `Legal.Credentials.IsLegalConfigured`) are deliberately NOT widened: their subject is the provider
   *   relationship and its credentials, which is not a tax adviser’s business and is the one surface Feature
   *   #241 keeps away from a customer-facing screen entirely.
   */
  static {
    PermissionService.document_shiftmaker_download.anyRoles = [Roles.ShiftLead];
    PermissionService.hubs_shiftmakerhub_pushliveupdate.anyRoles = [Roles.ShiftLead];
    PermissionService.legal_tss_getcurrent.anyRoles = [Roles.Steuerberater];
    PermissionService.legal_tss_history.anyRoles = [Roles.Steuerberater];
  }

  /**
   * The legacy endpoint-permission ids of the surface `CompanyBaseController` admits
   * {@link Roles.CompanyOwner} to — the SPA mirror of that one `[DynamicAuthorize("Company", MetaRole.Admin,
   * Roles.CompanyOwner)]` and of the nine actions that restate it at method level.
   *
   * Spelled out rather than derived from a `"Company."` prefix, because the prefix is **not** the boundary:
   * `Company.Company.List` and `Company.Setting.List` are `[NonAction]` overrides that no longer exist as
   * endpoints, `Company.Employee.Delete` names no action at all, and the two below are excluded deliberately.
   * A rule that says "anything starting with Company." would quietly claim all four.
   *
   * **Two of the 65 are left out on purpose**, by the same mechanical test the backend used — *does the action
   * narrow further than the company?* `Company.EmploymentRelationship.GetEventEmploymentCosts` and
   * `…GetEventsEmploymentCosts` are the only two that read shard-scoped data (an event lives in a per-shard
   * store), and they answer a company-only session with `E_SCOPE_SHARD_REQUIRED`
   * (`EmploymentRelationshipController.RequireShard`). A shard-context owner reaches them through `ShardAdmin`
   * as it always did; naming them here would tell the company-only session it may call something that refuses
   * it. Nothing in the SPA is affected either way — the two analytics sets that use them build their own
   * `PermissionSet` from `.default` — but the list is a statement about the backend, and a wrong statement here
   * is the kind that gets copied.
   *
   * @see [`CompanyBaseController`](../../../../../../API/Controller/Company/CompanyBaseController.cs)
   */
  private static readonly CompanyOwnedEndpoints: ReadonlySet<string> = new Set<string>([
    "Company.Company.GetCompanyShards",

    "Company.Employee.Add", "Company.Employee.AllowEmployeeLogin", "Company.Employee.ChangeName",
    "Company.Employee.EmployeeSalesList", "Company.Employee.EmployeeSalesOrders", "Company.Employee.Get",
    "Company.Employee.GetEmployeeShiftList", "Company.Employee.GetEmploymentRelationships",
    "Company.Employee.GetRestricted", "Company.Employee.GetStats", "Company.Employee.List",
    "Company.Employee.ListMinimal", "Company.Employee.ListNl", "Company.Employee.RequestVerification",
    "Company.Employee.ResetSocial", "Company.Employee.SetNewPassword",
    "Company.Employee.SetPublicProperty", "Company.Employee.SetSocial", "Company.Employee.SetValue",

    "Company.EmployeeAttribute.Add", "Company.EmployeeAttribute.ChangeName",
    "Company.EmployeeAttribute.ChangeShortName", "Company.EmployeeAttribute.Delete",
    "Company.EmployeeAttribute.Get", "Company.EmployeeAttribute.List",
    "Company.EmployeeAttribute.SetEmployeeAttributes",

    "Company.EmploymentRelationship.Add", "Company.EmploymentRelationship.ChangeName",
    "Company.EmploymentRelationship.Delete", "Company.EmploymentRelationship.Get",
    "Company.EmploymentRelationship.List", "Company.EmploymentRelationship.SetRelationship",
    "Company.EmploymentRelationship.SetValues",

    "Company.Setting.SetAuthPair", "Company.Setting.SetAuthPairs", "Company.Setting.SumUpIsConfigured",
    "Company.Setting.TelegramIsConfigured",

    "Company.Supplier.Add", "Company.Supplier.ChangeName", "Company.Supplier.Delete", "Company.Supplier.Get",
    "Company.Supplier.List", "Company.Supplier.Update",

    "Company.Telegram.AddBot", "Company.Telegram.AddChannel", "Company.Telegram.DeleteBot",
    "Company.Telegram.DeleteChannel", "Company.Telegram.GetBots", "Company.Telegram.GetChannelSettings",
    "Company.Telegram.List", "Company.Telegram.SetChannelSettings"
  ]);

  /**
   * Whether every id in `ids` sits on the {@link CompanyOwnedEndpoints} surface — i.e. whether a
   * {@link Roles.CompanyOwner} session may make every request the set stands for.
   *
   * Empty answers `false`, not vacuously `true`: an empty `default` already admits every signed-in session
   * through `CheckPermission`, and calling that "the company surface" would be a second way to say so.
   */
  static isCompanyOwnedSet(ids: readonly string[] | null | undefined): boolean {
    return !!ids?.length && ids.every(id => PermissionService.CompanyOwnedEndpoints.has(id));
  }

  /**
   * Adds {@link Roles.CompanyOwner} to every **generated** set whose whole `default` sits on the company
   * surface (#279).
   *
   * A grant-based session holds central roles and no granular ids at all, and every generated set carries
   * `_super: "ShardAdmin"` — which `RoleGrantExpander` withholds from a company-only context on purpose. So the
   * owner of a company matched neither half of any of these sets, and was hidden (and route-refused) from the
   * very screens `CompanyBaseController` had just been widened for.
   *
   * Mechanical rather than a hand-listed set of set names, because the id list above *is* the decision: a set
   * qualifies exactly when the backend admits every request it stands for. A hand-listed second copy would be
   * the thing that drifts. It runs in the same `static` block as the `ShiftLead` widenings and for the same
   * reason — the generated block between `copy start`/`copy end` is pasted from the backend generator, so an
   * edit inside it disappears at the next regeneration.
   *
   * It deliberately does **not** reach the hand-written `Permissions_*` / `CombinedPermission_*` sets: those
   * build a fresh `PermissionSet` from a generated set's `.default` on every call and therefore copy no
   * `anyRoles`. Each of them is a decision about a *screen* rather than an endpoint — whether the whole screen
   * works for this session, not just one request — so each names the role itself, or deliberately does not.
   */
  private static widenCompanySurfaceToOwner(): void {
    const service = PermissionService as unknown as Record<string, unknown>;
    for (const name of Object.getOwnPropertyNames(service)) {
      const set = service[name];
      if (!(set instanceof PermissionSet)) continue;
      if (!PermissionService.isCompanyOwnedSet(set.default)) continue;
      if (set.anyRoles.includes(Roles.CompanyOwner)) continue;
      set.anyRoles = [...set.anyRoles, Roles.CompanyOwner];
    }
  }

  /**
   * Runs {@link widenCompanySurfaceToOwner} — in its **own** block, below {@link CompanyOwnedEndpoints}.
   *
   * Static members initialise in declaration order, so a call from the `ShiftLead` block above would read the
   * id set as `undefined` and throw during module evaluation, taking the whole app down. The separation is
   * load-bearing, not tidiness.
   */
  static {
    PermissionService.widenCompanySurfaceToOwner();
  }


  /*




  COMPONENT PERMISSIONS




   */

  // navbar inventory

  // --> navbar combined
  /**
   * Whether the **Inventar** section appears at all.
   *
   * It must OR every entry the section actually contains, or an entry becomes unreachable without ever being
   * refused. `Permissions_SuppliersComponent` was missing (#279): *Lieferanten* is rendered by
   * `navbar-inventory.component.html`, not by the Unternehmen section its permission set is grouped with in
   * `Navbar_ShowCompany`. Nobody noticed because every session that passed the supplier set — a `ShardAdmin`,
   * through `_super` — passed the other six as well, so the section was always open for other reasons. It stops
   * being true the moment a session holds the supplier surface and nothing else: a company-only
   * `CompanyOwner` since this change, and a legacy custom role carrying only `Company.Supplier.List` all along.
   */
  Navbar_ShowInventory(): boolean {
    return this.CheckPermission(this.Permissions_CategoryComponent()) ||
      this.CheckPermission(this.Permissions_ItemComponent()) ||
      this.CheckPermission(this.Permissions_CompositionTypesComponent()) ||
      this.CheckPermission(this.Permissions_CompositionComponent()) ||
      this.CheckPermission(this.Permissions_TagsComponent()) ||
      this.CheckPermission(this.Permissions_ConfigurationsComponent()) ||
      this.CheckPermission(this.Permissions_SuppliersComponent())
  }

  static Permissions_CategoryComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_category_list.default);
    return set;
  }

  Permissions_CategoryComponent(): PermissionSet {
    return PermissionService.Permissions_CategoryComponent();
  }

  static Permissions_ItemComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_item_list.default);
    return set;
  }

  Permissions_ItemComponent(): PermissionSet {
    return PermissionService.Permissions_ItemComponent();
  }

  static Permissions_CompositionTypesComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_compositiontype_listnl.default)
    return set;
  }

  Permissions_CompositionTypesComponent(): PermissionSet {
    return PermissionService.Permissions_CompositionTypesComponent();
  }

  static Permissions_CompositionsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_composition_list.default);
    set.default = set.default.concat(PermissionService.inventory_compositiontype_listnl.default)
    set.default = set.default.concat(PermissionService.inventory_compositiontype_get.default)
    set.default = set.default.concat(PermissionService.inventory_tag_list.default)
    set.default = set.default.concat(PermissionService.inventory_item_list.default)
    return set;
  }

  Permissions_CompositionComponent(): PermissionSet {
    return PermissionService.Permissions_CompositionsComponent()
  }

  static Permissions_TagsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_tag_list.default);
    return set;
  }

  Permissions_TagsComponent(): PermissionSet {
    return PermissionService.Permissions_TagsComponent();
  }


  static Permissions_ConfigurationsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_configurationbuilder_list.default);
    return set;
  }

  Permissions_ConfigurationsComponent(): PermissionSet {
    return PermissionService.Permissions_ConfigurationsComponent();
  }

  static Permissions_Inventory_BusinessCasesComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_businesscase_list.default)
    return set;
  }


  Permissions_Inventory_BusinessCasesComponent(): PermissionSet {
    return PermissionService.Permissions_Inventory_BusinessCasesComponent();
  }

  // navbar location

  // --> navbar combined

  Navbar_ShowLocations(): boolean {
    return this.CheckPermission(this.Permissions_EventsComponent()) ||
      this.CheckPermission(this.Permissions_OrderComponent()) ||
      this.CheckPermission(this.Permissions_LocationsComponent()) ||
      this.CheckPermission(this.Permissions_DevicesComponent()) ||
      this.CheckPermission(this.Permissions_TableComponent()) ||
      this.CheckPermission(this.Permissions_PrinterComponent()) ||
      this.CheckPermission(this.Permissions_CashPointComponent())
  }

  static Permissions_CashPointCloseAction(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.device_cashpointentry_getsummary.default)
    set.default = set.default.concat(PermissionService.device_cashpointentry_getbusinesscasetypesummary.default)
    set.default = set.default.concat(PermissionService.device_cashpointentry_getlastclosed.default)
    set.default = set.default.concat(PermissionService.device_cashpointentry_close.default)
    return set;
  }

  Permissions_CashPointCloseAction(): PermissionSet {
    return PermissionService.Permissions_CashPointCloseAction();
  }

  static Permissions_CashPointComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.device_cashpointentry_list.default)
    set.default = set.default.concat(PermissionService.company_employee_listnl.default)
    return set;
  }

  Permissions_CashPointAssistantClosingComponent(): PermissionSet {
    return PermissionService.Permissions_CashPointsAssistantClosingComponent();
  }

  static Permissions_CashPointsAssistantClosingComponent(): PermissionSet {
    // todo niko: implement
    let set = PermissionService.Permissions_CashPointCloseAction();
    return set;
  }

  Permissions_CashPointComponent(): PermissionSet {
    return PermissionService.Permissions_CashPointComponent();
  }

  static Permissions_EventsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_list.default);
    return set;
  }

  Permissions_EventsComponent(): PermissionSet {
    return PermissionService.Permissions_EventsComponent()
  }

  static Permissions_EventDetailsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_getextendedevent.default);
    set.default = set.default.concat(PermissionService.inventory_location_get.default);
    return set;
  }

  Permissions_EventDetailsComponent(): PermissionSet {
    return PermissionService.Permissions_EventDetailsComponent();
  }

  static Permissions_OrderComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.booking_order_list.default)
    return set;
  }

  Permissions_OrderComponent(): PermissionSet {
    return PermissionService.Permissions_OrderComponent();
  }

  static Permissions_LocationsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_location_list.default)
    return set;
  }

  Permissions_LocationsComponent(): PermissionSet {
    return PermissionService.Permissions_LocationsComponent();
  }

  static Permissions_DevicesComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.device_information_list.default)
    set.default = set.default.concat(PermissionService.inventory_location_list.default)
    return set;
  }

  Permissions_DevicesComponent(): PermissionSet {
    return PermissionService.Permissions_DevicesComponent();
  }

  /**
   * The register-enrolment screen (#469, step 5).
   *
   * Gated on **central roles only**, with an empty `default`, because that is exactly what the endpoint behind
   * it is: `DeviceEnrollmentController` carries `[Authorize(Roles = PlatformOwner, CompanyOwner, ShardAdmin)]`
   * and no endpoint-permission id at all. Putting an id in `default` would be worse than useless — `default`
   * is AND-evaluated, so it would lock out the two grant-based roles (`PlatformOwner`, `CompanyOwner`) that
   * hold no legacy ids and are explicitly allowed on the wire.
   */
  static Permissions_DeviceEnrollmentComponent(): PermissionSet {
    return new PermissionSet(Roles.ShardAdmin, [],
      [Roles.PlatformOwner, Roles.CompanyOwner, Roles.ShardAdmin]);
  }

  Permissions_DeviceEnrollmentComponent(): PermissionSet {
    return PermissionService.Permissions_DeviceEnrollmentComponent();
  }

  /**
   * The register-credential screen (#469, step 7).
   *
   * Identical to {@link Permissions_DeviceEnrollmentComponent} and for the identical reason:
   * `DeviceCredentialController` carries the same `[Authorize(Roles = PlatformOwner, CompanyOwner,
   * ShardAdmin)]` and no endpoint-permission id at all. It is a **separate** set rather than a reuse of the
   * enrolment one so the two screens can be gated apart if either controller's `[Authorize]` ever moves —
   * a shared set would silently carry one screen's change onto the other.
   */
  static Permissions_DeviceCredentialsComponent(): PermissionSet {
    return new PermissionSet(Roles.ShardAdmin, [],
      [Roles.PlatformOwner, Roles.CompanyOwner, Roles.ShardAdmin]);
  }

  Permissions_DeviceCredentialsComponent(): PermissionSet {
    return PermissionService.Permissions_DeviceCredentialsComponent();
  }

  static Permissions_TableComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.booking_table_list.default)
    return set;
  }

  Permissions_TableComponent(): PermissionSet {
    return PermissionService.Permissions_TableComponent();
  }

  static Permissions_PrinterComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.device_printer_list.default)
    return set;
  }

  Permissions_PrinterComponent(): PermissionSet {
    return PermissionService.Permissions_PrinterComponent();
  }

  // navbar analytics

  // --> navbar analytics combined

  Navbar_ShowAnalytics(): boolean {
    return this.CheckPermission(PermissionService.Permissions_SingleAnalyticsComponent()) ||
      this.CheckPermission(PermissionService.Permissions_MultiAnalysisComponent())
  }

  static Permissions_SingleAnalyticsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_list.default)
    set.default = set.default.concat(PermissionService.inventory_location_list.default)
    set.default = set.default.concat(PermissionService.inventory_compositiontype_listnl.default)
    set.default = set.default.concat(PermissionService.company_employmentrelationship_geteventemploymentcosts.default)
    set.default = set.default.concat(PermissionService.inventory_item_geteventsourcingcosts.default)
    set.default = set.default.concat(PermissionService.inventory_event_getextendedevent.default)
    return set;
  }

  Permissions_SingleAnalyticsComponent(): PermissionSet {
    return PermissionService.Permissions_SingleAnalyticsComponent();
  }

  static Permissions_MultiAnalysisComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_location_list.default)
    set.default = set.default.concat(PermissionService.inventory_compositiontype_listnl.default)
    set.default = set.default.concat(PermissionService.analysis_eventanalysis_fetcheventdata.default)
    set.default = set.default.concat(PermissionService.company_employmentrelationship_geteventsemploymentcosts.default)
    return set;
  }

  Permissions_MultiAnalysisComponent(): PermissionSet {
    return PermissionService.Permissions_MultiAnalysisComponent();
  }

  // navbar automation

  // --> navbar automation combined
  Navbar_ShowAutomation(): boolean {
    return this.CheckPermission(PermissionService.Permissions_AutomationManagerComponent()) ||
      this.CheckPermission(PermissionService.Permissions_AutomationWorkflowsComponent());
  }

  static Permissions_AutomationManagerComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_list.default)
    set.default = set.default.concat(PermissionService.inventory_configuration_list.default)
    set.default = set.default.concat(PermissionService.inventory_composition_list.default)
    set.default = set.default.concat(PermissionService.inventory_compositiontype_list.default)
    set.default = set.default.concat(PermissionService.automation_scheduler_getschedulerobjects.default)
    set.default = set.default.concat(PermissionService.automation_scheduler_schedulegroup.default)
    set.default = set.default.concat(PermissionService.automation_scheduler_deletegroup.default)
    return set;
  }

  Permissions_AutomationManagerComponent(): PermissionSet {
    return PermissionService.Permissions_AutomationManagerComponent();
  }

  static Permissions_AutomationWorkflowsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default);
    return set;
  }

  Permissions_AutomationWorkflowsComponent(): PermissionSet {
    return PermissionService.Permissions_AutomationWorkflowsComponent();
  }

  // not in navbar
  static Permissions_AutomationViewComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.hubs_automationhub_subscribe.default);
    set.default = set.default.concat(PermissionService.automation_scheduler_getschedulergroup.default)
    set.default = set.default.concat(PermissionService.automation_scheduler_getschedulerobjects.default)
    return set;
  }

  // navbar legal

  // --> navbar legal combined
  Navbar_ShowLegal(): boolean {
    return this.CheckPermission(PermissionService.Permissions_LegalSettingsComponent()) ||
      this.CheckPermission(PermissionService.Permissions_LegalTSEComponent()) ||
      this.CheckPermission(PermissionService.Permissions_LegalExportsComponent()) ||
      this.CheckPermission(PermissionService.Permissions_LegalTseExportsComponent()) ||
      this.CheckPermission(PermissionService.Permissions_LegalClosingCorrectionsComponent())
  }

  static Permissions_LegalSettingsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default)
    return set;
  }

  Permissions_legalSettingsComponent(): PermissionSet {
    return PermissionService.Permissions_LegalSettingsComponent();
  }

  /**
   * The TSE page (`/legal/tss`). `ShardAdmin` as it always was, plus {@link Roles.Steuerberater} (#769) — the
   * company’s tax adviser, whose whole engagement is this fiscal record.
   *
   * The role goes in `anyRoles`, not in `default`: `default` is AND-evaluated, so naming it there would make it
   * REQUIRED of every shard administrator that reaches the page today. Mirrors
   * `TSSController.GetCurrent`/`History`, which admit exactly `ShardAdmin` (through the inherited
   * `MetaRole.Admin`) and `Steuerberater`.
   *
   * {@link Permissions_LegalSettingsComponent} is deliberately left alone, which is what keeps the
   * **Rechtliches → Einstellungen** entry out of an adviser’s navigation: the provider relationship and its
   * credentials are not theirs, and that screen is the one place in this area that names a supplier at all.
   */
  static Permissions_LegalTSEComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default, [Roles.Steuerberater])
    return set;
  }

  Permissions_LegalTSEComponent(): PermissionSet {
    return PermissionService.Permissions_LegalTSEComponent();
  }

  /**
   * The DSFinV-K data export (`/legal/exports`, #770) — creating the machine-readable record of a period and
   * downloading it.
   *
   * Same shape and same reasoning as {@link Permissions_LegalTSEComponent}: `ShardAdmin` through `default`,
   * plus {@link Roles.Steuerberater} in `anyRoles` because an adviser session carries that one central role
   * and no legacy endpoint-permission id at all, and `default` is AND-evaluated — naming the role there would
   * make it REQUIRED of every shard administrator who reaches the screen today.
   *
   * It has to match `FiscalExportController`'s own gate. A navigation entry that is narrower than the surface
   * hides a screen somebody may use; one that is wider shows a screen whose every request answers 403, which
   * is the worse half because it looks like a fault rather than a permission.
   */
  static Permissions_LegalExportsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default, [Roles.Steuerberater])
    return set;
  }

  Permissions_LegalExportsComponent(): PermissionSet {
    return PermissionService.Permissions_LegalExportsComponent();
  }

  /**
   * The TSE data export (`/legal/tse-exports`, #776) — producing the machine-readable record of the security
   * device's own signed log messages and downloading it.
   *
   * Same shape and same reasoning as {@link Permissions_LegalExportsComponent}: `ShardAdmin` through
   * `default`, plus {@link Roles.Steuerberater} in `anyRoles` because an adviser session carries that one
   * central role and no legacy endpoint-permission id at all, and `default` is AND-evaluated — naming the
   * role there would make it REQUIRED of every shard administrator who reaches the screen today.
   *
   * It has to match `FiscalTseExportController`'s own gate, which is the same set `FiscalJobController` and
   * `FiscalExportController` carry. A navigation entry that is narrower than the surface hides a screen
   * somebody may use; one that is wider shows a screen whose every request answers 403, which is the worse
   * half because it looks like a fault rather than a permission.
   */
  static Permissions_LegalTseExportsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default, [Roles.Steuerberater])
    return set;
  }

  Permissions_LegalTseExportsComponent(): PermissionSet {
    return PermissionService.Permissions_LegalTseExportsComponent();
  }

  /**
   * Correcting filed cash point closings (`/legal/closing-corrections`, #771).
   *
   * The same set as {@link Permissions_LegalExportsComponent}, because the story's gate is the same four roles
   * #770 admits (PlatformOwner, CompanyOwner, ShardAdmin, Steuerberater): `ShardAdmin` through `default`, and
   * {@link Roles.Steuerberater} in `anyRoles`, since an adviser session carries only that central role. It has
   * to match the closing-correction controller's own gate, for the reason recorded on the TSE export set above.
   */
  static Permissions_LegalClosingCorrectionsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default, [Roles.Steuerberater])
    return set;
  }

  Permissions_LegalClosingCorrectionsComponent(): PermissionSet {
    return PermissionService.Permissions_LegalClosingCorrectionsComponent();
  }

  // navbar company

  // --> navbar company combined

  Navbar_ShowCompany(): boolean {
    return this.IsPayroll() ||
      this.CheckPermission(PermissionService.Permissions_EmployeeComponent()) ||
      this.CheckPermission(PermissionService.Permissions_EmploymentRelationshipComponent()) ||
      this.CheckPermission(PermissionService.Permissions_ShiftMakerComponent()) ||
      this.CheckPermission(PermissionService.Permissions_SuppliersComponent()) ||
      this.CheckPermission(PermissionService.Permissions_TelegramComponent()) ||
      this.CheckPermission(PermissionService.Permissions_CompanySettingsComponent()) ||
      this.CheckPermission(PermissionService.Permissions_ContractComponent())
  }

  /**
   * The Verträge screen. **Now widened to {@link Roles.CompanyOwner}** (#279), reversing the decision recorded
   * here when only `CompanyBaseController` had moved.
   *
   * Its single load request is `Administration/Contract/GetCurretCompany`, and
   * `AdministrationBaseController` now names the role. The action needs no shard either — it iterates the
   * company's own shards rather than reading the session's — so the company-only session that carries this
   * role gets the whole screen, not most of it.
   *
   * The role goes in `anyRoles`; `default` is left as the bare `"ShardAdmin"` it always was. Replacing it with
   * the real endpoint id would ALSO admit any legacy custom role holding that id, which is a different and
   * larger decision than the one being made here.
   */
  static Permissions_ContractComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default, [Roles.CompanyOwner])
    return set;
  }

  Permissions_ContractComponent(): PermissionSet {
    return PermissionService.Permissions_ContractComponent();
  }

  /**
   * The Mitarbeiter screen (#279 — `CompanyOwner`).
   *
   * Every id here is on the widened `CompanyBaseController` surface, and so is everything the screen goes on to
   * call: the add/rename/end-employment/rehire writes, the relationship and attribute popups, and the worktime
   * stats. The one thing it offers that is **not** — the Excel export
   * (`Document/EmployeeController.Download`, still `MetaRole.Admin`) — is gated separately on
   * {@link Document_Employee_Download}, which is not widened, so the button is simply absent for an owner
   * rather than present and 403-ing.
   */
  static Permissions_EmployeeComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employee_list.default, [Roles.CompanyOwner])
    set.default = set.default.concat(PermissionService.company_employmentrelationship_list.default)
    set.default = set.default.concat(PermissionService.company_employeeattribute_list.default)
    return set;
  }

  Permissions_EmployeeComponent(): PermissionSet {
    return PermissionService.Permissions_EmployeeComponent();
  }

  /**
   * The Mitarbeiterprofil screen (not in the navbar — reached from a row of the Mitarbeiter list).
   *
   * Widened to {@link Roles.CompanyOwner} (#279) because every section it loads is on the widened surface:
   * the employee read, sales, shifts, employment relationships and stats. It has to move with
   * {@link Permissions_EmployeeComponent} — opening the list and refusing the row it links to would be a dead
   * end on every click.
   *
   * **The 403 this used to describe is gone, and #418 with it.** The authenticator block called
   * `GET /auth/account/admin/external-identities` for everybody, and that endpoint is
   * {@link Roles.PlatformOwner} only — so a `ShardAdmin` or `CompanyOwner` opening this screen fired a
   * guaranteed 403 into `console.error` and got an empty identity picker. The note here said fixing it was a
   * decision about who may enumerate the installation's identities, and that decision has since been made: the
   * answer is nobody but a platform owner, for disclosure *and* for consent (linking by id binds a person's
   * identity to a staff record without that person acting). The picker and its directory read are now behind
   * {@link CanLinkEmployeeAuthenticatorById}; every other administering role issues an invitation the employee
   * redeems by signing in themselves. Nothing on this screen 403s for the audience it is rendered to.
   */
  static Permissions_EmployeeDetailsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employee_get.default, [Roles.CompanyOwner])
    set.default = set.default.concat(PermissionService.company_employee_employeesaleslist.default)
    return set;
  }

  Permissions_EmployeeDetailsComponent(): PermissionSet {
    return PermissionService.Permissions_EmployeeDetailsComponent();
  }

  /**
   * The Arbeitsverhältnisse screen, and — through `company.routes.ts` — the Lohnbuchhalter screen, which shares
   * this audience.
   *
   * Widened to {@link Roles.CompanyOwner} (#279). Both are clean for a company-only session: the list and its
   * add/rename/delete writes are on the widened `EmploymentRelationshipController`, and the invitations screen
   * talks to `PayrollInvitationController`, which has named `PlatformOwner,CompanyOwner,ShardAdmin` since it
   * was written. The screen's export button reads `Document/EmploymentRelationshipController`
   * (`MetaRole.Admin`) and is gated on its own un-widened set, so it stays hidden.
   *
   * Note this set does **not** carry `GetEventEmploymentCosts`, the one part of that controller a company-only
   * session cannot reach — see {@link CompanyOwnedEndpoints}.
   */
  static Permissions_EmploymentRelationshipComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employmentrelationship_list.default,
      [Roles.CompanyOwner])
    return set;
  }

  Permissions_EmploymentRelationshipComponent(): PermissionSet {
    return PermissionService.Permissions_EmploymentRelationshipComponent();
  }

  /**
   * Who may reach the shift plan ("Dienstplan") — the exact set `ShiftPlanController.ReadRoles` gates its one
   * endpoint on: {@link Roles.PlatformOwner}, {@link Roles.CompanyOwner}, {@link Roles.ShardAdmin} and
   * {@link Roles.ShiftLead}.
   *
   * **Why the six legacy granular ids are gone (#409).** They named the endpoints the screen used to read —
   * `Inventory.Location.GetCompanyLocations`, `Company.Employee.ListNl`, the three `Inventory.Event.*` reads and
   * `Administration.Settings.GetShiftMakerSettings`. The screen calls none of them any more; it reads
   * `/v2026.8/Company/ShiftPlan/*`. Leaving them in `default` would have kept AND-demanding permissions that no
   * longer buy anything, and — worse — would have shown the menu entry to a granular-only session that the new
   * endpoint then refuses. A set that asserts requirements the screen does not have is a set that lies in both
   * directions.
   *
   * **Why `default` is not simply empty.** `CheckPermission` ends in `default.every(...)`, and `[].every(...)`
   * is `true` — an empty list would grant the screen to *everyone*, including a plain employee. It therefore
   * carries the same role as `_super`, which makes the AND-fallback and the OR short-circuit agree instead of
   * contradicting each other.
   *
   * **The entry is role-gated while the endpoint is role-AND-window gated, on purpose.** The real permission is
   * the role plus the running event's time window (`IShiftLeadGuard` / `ShiftLeadAccess`), but a navbar can only
   * see roles. Making the entry appear and disappear on its own would need a backend question on every render
   * and would make the menu move under the user; a shift lead who opens it outside a window instead gets the
   * `E_SHIFT_LEAD_OUTSIDE_WINDOW` / `E_SHIFT_LEAD_NO_CURRENT_EVENT` refusal, which the screen now surfaces
   * verbatim. A visible door that says why it is locked beats a door that is sometimes not there.
   *
   * Note this also fixes a quieter gap: a session holding only {@link Roles.PlatformOwner} or
   * {@link Roles.CompanyOwner} (which is how a grant-based session holds them — central roles, no granular ids)
   * matched neither the old `_super` nor the old `default`, so it never saw this entry at all.
   */
  static Permissions_ShiftMakerComponent(): PermissionSet {
    return new PermissionSet(Roles.ShardAdmin, [Roles.ShardAdmin],
      [Roles.PlatformOwner, Roles.CompanyOwner, Roles.ShiftLead]);
  }

  Permissions_ShiftMakerComponent(): PermissionSet {
    return PermissionService.Permissions_ShiftMakerComponent();
  }

  /**
   * The Lieferanten screen and its settings page. Widened to {@link Roles.CompanyOwner} (#279): both are wholly
   * served by the widened `SupplierController` — list, get, add, rename, delete — with no second controller
   * behind either of them.
   */
  static Permissions_SuppliersComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_supplier_list.default,
      [Roles.CompanyOwner])
    return set;
  }

  Permissions_SuppliersComponent(): PermissionSet {
    return PermissionService.Permissions_SuppliersComponent();
  }

  /**
   * The Telegram screen. Widened to {@link Roles.CompanyOwner} (#279).
   *
   * The bot token is a **company**-scope secret in `SecretCatalog`, so the owner is if anything its more
   * natural custodian than the admin of one shard; `AuthUnit.Value` is `[JsonIgnore]`d, so neither `GetBots`
   * nor `AddBot` serialises it. The channel editor additionally reads
   * `Company.Company.GetCompanyShards` — also on the widened surface, and a company-scoped read that *returns*
   * the shard list rather than requiring a shard claim, which is what makes it work without one.
   */
  static Permissions_TelegramComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_telegram_getbots.default,
      [Roles.CompanyOwner])
    return set;
  }

  Permissions_TelegramComponent(): PermissionSet {
    return PermissionService.Permissions_TelegramComponent();
  }

  /**
   * The Unternehmenseinstellungen screen. **Now widened to {@link Roles.CompanyOwner}** (#279), reversing the
   * decision recorded here when only half of what it does had been widened.
   *
   * All four of its load requests now succeed for a company-only owner: the SumUp and Telegram probes are
   * `Company.Setting.*` on the widened `SettingController`, and `GetTimezone` / `GetShiftMakerSettings` — plus
   * the `SetTimezone` / `SetShiftMakerSettings` saves — are on `AdministrationBaseController`, which now names
   * the role. None of the four needs a shard.
   *
   * **The logo is not a counter-example.** `Administration/Settings/SetLogo` IS shard-scoped and refuses a
   * company-only session with `E_SCOPE_SHARD_REQUIRED`, but this screen never calls it: `loadLogoImage()` has
   * an empty body and the whole Logo block in the template is commented out. If that block is ever restored,
   * this set has to be revisited — the screen would then have a request its audience cannot make.
   *
   * The role goes in `anyRoles`; `default` stays the bare `"ShardAdmin"`. See
   * {@link Permissions_ContractComponent} for why the real ids are deliberately not put there.
   */
  static Permissions_CompanySettingsComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default, [Roles.CompanyOwner])
    return set;
  }

  Permissions_CompanySettingsComponent(): PermissionSet {
    return PermissionService.Permissions_CompanySettingsComponent();
  }

  /**
   * The **account-security policy panel** on the Unternehmenseinstellungen screen (#282) — one company-wide
   * setting, deliberately gated NARROWER than the screen that carries it.
   *
   * `Permissions_CompanySettingsComponent` above admits a `ShardAdmin`, because everything else on that screen
   * (timezone, Dienstplan rounding) is administered by one. This panel's two endpoints
   * (`AccountController`'s `admin/companies/{companyId}/security-level`, GET and POST) name only
   * `PlatformOwner` and `CompanyOwner`, and the controller records why: the level is a policy over every
   * account that can enter the company, so a shard's administrator raising it would bind colleagues in shards
   * they have nothing to do with, and lowering it would silently drop the whole company's bar.
   *
   * Rendering it for a `ShardAdmin` anyway would produce a control whose every save answers 403 — worse than
   * no control, because it looks like a setting they own. Hence its own set rather than reusing the screen's.
   *
   * **Shape:** `_super` + `default` both name {@link Roles.PlatformOwner} and `anyRoles` names
   * {@link Roles.CompanyOwner} — the same shape as {@link owner}, and the reason `default` is not left empty is
   * the invariant pinned in `permission.service.spec.ts`: `[].every()` is `true`, so an empty `default` without
   * `anyRoles` would pass for every signed-in session. Note the endpoint's *second* gate,
   * `IsOwnCompanyOrPlatform(companyId)`, has no SPA equivalent and needs none: the panel only ever sends the
   * company its own session names.
   */
  static Permissions_CompanyAccountSecurityPanel(): PermissionSet {
    return new PermissionSet(Roles.PlatformOwner, [Roles.PlatformOwner], [Roles.CompanyOwner]);
  }

  Permissions_CompanyAccountSecurityPanel(): PermissionSet {
    return PermissionService.Permissions_CompanyAccountSecurityPanel();
  }

  // navbar admin

  // --> navbar admin combined

  Navbar_ShowAdmin(): boolean {
    return this.CheckPermission(PermissionService.Permissions_AdminUsersComponent()) ||
      this.CheckPermission(PermissionService.Permissions_AdminPatchesComponent())
  }

  static Permissions_AdminUsersComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default)
    return set;
  }

  static Permissions_AdminPatchesComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.admin.default)
    return set;
  }

  // navbar owner

  // --> navbar owner combined

  Navbar_ShowOwner(): boolean {
    return this.CheckPermission(PermissionService.Permissions_Owner())
  }

  /**
   * The navbar's platform-owner gate. It rebuilds {@link PermissionService.owner} with an explicit
   * `PlatformOwner` `_super` — which was the *only* thing keeping the navbar honest while that set carried a
   * `ShardAdmin` one. Now that both agree, this is a copy rather than a correction; kept because every navbar
   * group is reached through a `Permissions_*` method, and the two are pinned equal in the spec so they cannot
   * drift apart again.
   */
  static Permissions_Owner(): PermissionSet {
    return new PermissionSet(Roles.PlatformOwner, PermissionService.owner.default);
  }

  // navbar help

  // --> navbar help combined

  static Permissions_ChangelogComponent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.miscellaneous_changelog_list.default);
    return set;
  }


  // ------------------------------------------------------------------------------------------


  /*




  COMBINED PERMISSIONS




   */

  CombinedPermission_CompositionCreate(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_composition_create.default)
    set.default = set.default.concat(PermissionService.inventory_tag_list.default);
    set.default = set.default.concat(PermissionService.inventory_item_list.default);
    return set;
  }

  CombinedPermission_ItemAdd(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_item_add.default);
    set.default = set.default.concat(PermissionService.inventory_category_list.default);
    return set;
  }

  CombinedPermission_CompositionSetCompositionType(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_compositiontype_setcompositiontype.default);
    set.default = set.default.concat(PermissionService.inventory_compositiontype_listnl.default);
    return set;
  }

  CombinedPermission_TagSetFilter(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_tag_setfilter.default);
    set.default = set.default.concat(PermissionService.inventory_category_list.default)
    return set;
  }

  CombinedPermission_TagRedirectComposition(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.Permissions_CompositionsComponent().default)
    set.default = set.default.concat(PermissionService.inventory_tag_get.default);
    return set;
  }

  CombinedPermission_LocationReadConfiguration(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_configuration_list.default);
    return set;
  }

  CombinedPermission_LocationSetConfiguration(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", this.CombinedPermission_LocationReadConfiguration().default);
    set.default = set.default.concat(PermissionService.inventory_location_setlocationconfig.default)
    set.default = set.default.concat(PermissionService.inventory_location_pushlocation.default)
    return set;
  }

  CombinedPermission_ConfigurationBuilderEdit(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_configurationbuilder_list.default);
    set.default = set.default.concat(PermissionService.inventory_configurationbuilder_setspecial.default)
    set.default = set.default.concat(PermissionService.inventory_override_tag.default)
    set.default = set.default.concat(PermissionService.inventory_override_composition.default)
    return set;
  }

  CombinedPermission_EventGetExtendedEvent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_getextendedevent.default);
    set.default = set.default.concat(PermissionService.inventory_location_get.default);
    return set;
  }

  CombinedPermission_EventDownloadExtendedEvent(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", this.CombinedPermission_EventGetExtendedEvent().default)
    set.default = set.default.concat(PermissionService.inventory_event_download.default);
    set.default = set.default.concat(PermissionService.company_supplier_list.default)
    return set;
  }

  CombinedPermission_EventReadLocationConfig(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", this.CombinedPermission_EventGetExtendedEvent().default);
    set.default = set.default.concat(PermissionService.inventory_location_list.default)
    set.default = set.default.concat(PermissionService.inventory_configuration_list.default)
    return set;
  }

  CombinedPermission_EventSetLocationConfig(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", this.CombinedPermission_EventReadLocationConfig().default);
    set.default = set.default.concat(PermissionService.inventory_event_setlocationconfigs.default)
    return set;
  }

  CombinedPermission_EventAdd(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_add.default);
    set.default = set.default.concat(PermissionService.inventory_event_addmany.default);
    return set;
  }

  CombinedPermission_EventAddConfigured(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", this.Inventory_Event_Add().default);
    set.default = set.default.concat(PermissionService.inventory_event_addmany.default);
    set.default = set.default.concat(PermissionService.inventory_location_list.default);
    set.default = set.default.concat(PermissionService.inventory_configuration_list.default);
    return set;
  }

  /** Assigning an employee's relationship from the Mitarbeiter list — both ids widened (#279). */
  CombinedPermission_EmployeeEditEmployeeRelationship(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employmentrelationship_list.default,
      [Roles.CompanyOwner])
    set.default = set.default.concat(PermissionService.company_employmentrelationship_setrelationship.default)
    return set;
  }

  /** The Telegram channel editor — `GetChannelSettings` + `GetCompanyShards`, both widened (#279). */
  CombinedPermission_TelegramViewChannelConfiguration(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_telegram_getchannelsettings.default,
      [Roles.CompanyOwner]);
    set.default = set.default.concat(PermissionService.company_company_getcompanyshards.default);
    return set;
  }

  /** Saving that editor — the reads above plus `SetChannelSettings`, all widened (#279). */
  CombinedPermission_TelegramEditChannelConfiguration(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", this.CombinedPermission_TelegramViewChannelConfiguration().default,
      [Roles.CompanyOwner]);
    set.default = set.default.concat(PermissionService.company_telegram_setchannelsettings.default);
    return set;
  }

  /** An employee's Telegram link on the Mitarbeiter settings page — `SetSocial`/`ResetSocial`, widened (#279). */
  CombinedPermission_EmployeeTelegram(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employee_setsocial.default,
      [Roles.CompanyOwner]);
    set.default = set.default.concat(PermissionService.company_employee_resetsocial.default);
    return set;
  }

  CombinedPermission_EventSetPublicProperties(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_setpublicvariable.default)
    set.default = set.default.concat(PermissionService.inventory_event_changepublicresponsetime.default);
    return set;
  }

  CombinedPermission_HomeEventPermission(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.inventory_event_list.default);
    set.default = set.default.concat(PermissionService.inventory_event_getcurrentornext.default)
    return set;
  }

  /** The Verkäufe tab of a Mitarbeiterprofil — both sales reads are widened (#279). */
  CombinedPermission_EmployeeSalesView(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employee_employeesaleslist.default,
      [Roles.CompanyOwner]);
    set.default = set.default.concat(PermissionService.company_employee_employeesalesorders.default)
    return set;
  }

  /**
   * The Informationen tab of a Mitarbeiterprofil, its mobile `<option>`, and — through
   * {@link CanViewEmployeeSubmissions} — the Formulare tab.
   *
   * **Now widened to {@link Roles.CompanyOwner} (#279), reversing the decision recorded when
   * {@link CanManageEmployeeAuthenticators} was written.** That decision was right at the time and its reason
   * has since gone: it said the shared set must stay `ShardAdmin`-only because these tabs read
   * `EmployeeController`, "whose base `CompanyBaseController` carries `[DynamicAuthorize("Company",
   * MetaRole.Admin)]` — ShardAdmin and nothing else", so widening would render tabs whose every request 403s.
   * That base now names `Roles.CompanyOwner`, and the requests behind the tabs succeed.
   *
   * The endpoint is the unversioned `GET /Company/Employee/GetStats/{employee}`, on the widened controller —
   * **not** `v2025_4/EmployeeController.GetStats`, which shares the name and the legacy id but hangs off
   * `VersionedCompanyBaseController` (still `MetaRole.Admin`) and is called by nothing in the SPA. Two actions
   * collapsing onto one permission id is exactly how a widening gets claimed for a gate that never moved.
   *
   * {@link CanManageEmployeeAuthenticators} stays a separate helper regardless: it mirrors four actions on
   * `AccountController`, a different controller with its own `[Authorize]` list, and it must keep refusing a
   * `Payroll` session that this set (through `CanViewEmployeeSubmissions`) admits.
   */
  CombinedPermission_EmployeeDetailsView(): PermissionSet {
    let set = new PermissionSet("ShardAdmin", PermissionService.company_employee_getstats.default,
      [Roles.CompanyOwner]);
    return set;
  }

  static = new PermissionSet(
    "",
    [""]
  )

  // employee
  GetEmployeeBasePermission(): PermissionSet {
    return PermissionService.ep_base;
  }

  static ep_base = new PermissionSet(
    "_none",
    ["Employee"]
  )


  constructor(private authenticationService: AuthenticationService) {

  }

  /**
   * Whether the current session satisfies `permissions`.
   *
   * Three checks, in widening-to-narrowing order:
   * 1. the single `_super` **central role** supersedes everything (historically `ShardAdmin`);
   * 2. `anyRoles` — further central roles, **any one** of which is enough (a set may legitimately serve two
   *    unrelated audiences, e.g. the shift plan's `ShardAdmin` *or* `ShiftLead`, #280);
   * 3. `default` — the legacy endpoint-permission ids, **all** of which must be held.
   *
   * `anyRoles` is read defensively (`?.`): most call sites construct a real {@link PermissionSet}, but a few
   * specs and Storybook decorators hand in object literals shaped like one.
   */
  CheckPermission(permissions: PermissionSet): boolean {
    const user = this.authenticationService.userValue;
    if (user == null) return false;
    if (user.permissions.includes(permissions._super)) return true;
    if (permissions.anyRoles?.some((role: string) => user.permissions.includes(role))) return true;
    return permissions.default.every((x: string) => user.permissions.includes(x));
  }

  /**
   * True when the current session carries the external-only {@link Roles.Payroll} role (a "Lohnbuchhalter").
   * Such a session holds *only* the Payroll permission (plus optionally SensitiveDataAccessor), so the regular
   * {@link CheckPermission} sets — all keyed on ShardAdmin/admin defaults — never match it. The restricted
   * payroll view therefore gates its handful of allowed surfaces (employee list/detail, employment
   * relationships, Zeiten export) on this helper instead.
   */
  IsPayroll(): boolean {
    const user = this.authenticationService.userValue;
    return user != null && user.permissions.includes(Roles.Payroll);
  }

  /**
   * True when the current session carries the standalone {@link Roles.Operator} baseline role. Like a payroll
   * session, an operator session holds *only* that one permission, so every regular `CheckPermission` set — all
   * keyed on the ShardAdmin/admin defaults — misses it; the operator surfaces gate on this helper instead.
   *
   * Note this is deliberately NOT "the session may see the live screens": an admin can see them too. It answers
   * "is this session an operator", which is what the minimal operator shell and its landing route need.
   */
  IsOperator(): boolean {
    const user = this.authenticationService.userValue;
    return user != null && user.permissions.includes(Roles.Operator);
  }

  /**
   * True when the session may view an employee's Formulare (submissions) tab: the regular employee-detail
   * viewers (ShardAdmin & co), plus an external {@link Roles.Payroll} accountant — reviewing submissions is the
   * core of the payroll flow, yet a payroll session holds none of the admin detail permissions.
   */
  CanViewEmployeeSubmissions(): boolean {
    return this.CheckPermission(this.CombinedPermission_EmployeeDetailsView()) || this.IsPayroll();
  }

  /**
   * True when the session may manage an employee's **external authenticators** — the "Anmeldung" tab on
   * `employee-details`: which accounts may sign in as that employee, and whether their password login is on.
   *
   * Written against the **central role vocabulary** rather than as a `PermissionSet`, and mirroring
   * `AccountController`'s four `admin/employees/…` actions exactly (`PlatformOwner`, `CompanyOwner`,
   * `ShardAdmin`). The backend widened those to `CompanyOwner` because a company-only owner session carries
   * `CompanyOwner` **without** `ShardAdmin` — `RoleGrantExpander` produces the expansion only when a shard is
   * selected — and every `PermissionSet` in this file carries `_super: "ShardAdmin"`, so none of them can match
   * such a session.
   *
   * **Why this stays its own helper even now that {@link CombinedPermission_EmployeeDetailsView} names the
   * role too.** The original reason has expired: that set was kept `ShardAdmin`-only because its tabs read
   * `EmployeeController`, whose base was `ShardAdmin` and nothing else, so widening it would have rendered tabs
   * that 403 — and `CompanyBaseController` has since been widened, which is what made the rest of this change
   * possible. The remaining reason is different and does not expire: these four actions are on
   * **`AccountController`**, a different controller with its own `[Authorize]` list that can move
   * independently, and this helper must keep **refusing a `Payroll` session** that the detail set admits
   * through {@link CanViewEmployeeSubmissions}. Binding somebody's login to a staff record is not bookkeeping,
   * and folding the two together would lose that distinction on the first edit to either.
   */
  CanManageEmployeeAuthenticators(): boolean {
    const user = this.authenticationService.userValue;
    if (user == null) return false;
    return user.permissions.includes(Roles.PlatformOwner)
      || user.permissions.includes(Roles.CompanyOwner)
      || user.permissions.includes(Roles.ShardAdmin);
  }

  /**
   * True when the session may attach an **existing** external identity to an employee by picking it out of a
   * list — {@link Roles.PlatformOwner} alone, mirroring `AccountController.LinkEmployeeAuthenticator`.
   *
   * Narrower than {@link CanManageEmployeeAuthenticators} on purpose, and the gap is the whole point: everyone
   * who passes that one administers their own staff's logins, but only a platform owner may *enumerate* the
   * installation's accounts. The picker cannot exist without that enumeration
   * (`GET /auth/account/admin/external-identities`), and naming an account also binds a person's identity to a
   * staff record without that person doing anything.
   *
   * **This is what makes the picker absent rather than broken.** Before the split it offered a select whose
   * options came from a call that 403s for every non-owner — a control that could not be operated (#418).
   * Everybody else gets the invitation instead: they issue a code, the employee signs in with their own
   * account, and the redemption makes the link. A control that 403s is worse than a control that is absent.
   */
  CanLinkEmployeeAuthenticatorById(): boolean {
    return this.authenticationService.userValue?.permissions.includes(Roles.PlatformOwner) === true;
  }

  /**
   * True when the session may manage scoped role grants (`company/access`, #261/#279).
   *
   * Written against the **central role vocabulary** rather than a `PermissionSet`, deliberately: every
   * `PermissionSet` in this file carries `_super: "ShardAdmin"` plus a legacy endpoint-permission `default`,
   * so none of them matches a `CompanyOwner` or a platform-only session — the two audiences this screen exists
   * for. Mirrors `RoleGrantController`'s `[Authorize]` list; `GrantIssuancePolicy` is the real gate and
   * narrows further per request, which the screen renders as a message rather than a failure.
   */
  CanManageAccessGrants(): boolean {
    const user = this.authenticationService.userValue;
    if (user == null) return false;
    return user.permissions.includes(Roles.PlatformOwner)
      || user.permissions.includes(Roles.CompanyOwner)
      || user.permissions.includes(Roles.ShardAdmin);
  }

  /**
   * True when the session may see an employee's **contact quick links** — the `cqme-contact-chip` placements
   * on the employee roster, the employee detail header and the shift plan (#558).
   *
   * Mirrors `GET /company/contact-link/for-employees` exactly: the three administering roles
   * {@link CanManageAccessGrants} already names, **plus {@link Roles.ShiftLead}**.
   *
   * **That fourth role is the whole reason this is not just `CanManageAccessGrants()`.** A Schichtleitung is
   * the person who actually needs to reach somebody — it is 22:40, a shift is short, and the roster is the
   * only place the handle exists — but they may not see, and the server will not tell them, who is out of
   * compliance with a contact requirement. Reusing the requirement screen's gate here would either lock the
   * shift lead out of the one surface built for them, or (widened) hand them the compliance overview as well.
   * Two different questions, two predicates, pinned separately in `permission.service.spec.ts`.
   *
   * Written against the **central role vocabulary** rather than as a `PermissionSet`, for the same reason
   * {@link CanManageAccessGrants} is: every set in this file carries `_super: "ShardAdmin"` plus a legacy
   * endpoint-permission `default`, and a grant-based `CompanyOwner` / `ShiftLead` session holds central roles
   * only — it would match neither half.
   */
  CanUseContactQuickLinks(): boolean {
    const user = this.authenticationService.userValue;
    if (user == null) return false;
    return user.permissions.includes(Roles.PlatformOwner)
      || user.permissions.includes(Roles.CompanyOwner)
      || user.permissions.includes(Roles.ShardAdmin)
      || user.permissions.includes(Roles.ShiftLead);
  }
}
